/**
 * Document Structure Builder
 *
 * Builds the structure of migration documentation.
 */

import type { AgolResolution, GeoEventConfiguration, Input, Output, ServiceNode, PropertyMap, ArcGISServerConnection, FolderDataStore } from '../types/geoevent.js';
import type { VelocityConfiguration, TargetPlatform } from '../types/velocity.js';
import type { ConfigurationMappingResult, MappingResult } from '../types/mapping.js';
import type { MermaidDiagram } from '../types/mermaid.js';
import type { MigrationStrategy } from '../types/api.js';
import type { ServiceGraph } from '../types/graph.js';
import type { DocumentSection, DocumentSectionComponentType, DocumentSectionStatus, DocumentTable, DocumentTableRow, MigrationDocument, MigrationDocumentOptions } from '../types/generator.js';
import type { MigrationReportModel, ReportSection as RmSection, DiagramAsset as RmDiagramAsset, LinkedItem as RmLinkedItem, DiagramGraph as RmDiagramGraph } from '../types/render-model.js';
import { MAX_CELL_TEXT } from '../types/render-model.js';
import { detectCustomComponents } from '../parser/custom-detector.js';
import { renderServiceGraph } from '../renderer/mermaid-renderer.js';
import { buildServiceGraph } from '../sorter/graph-builder.js';
import { topologicalSort } from '../sorter/topological-sort.js';
import { redactSensitiveText, redactSensitiveValue } from '../security/redaction.js';
import { getGeoEventComponentLabel, getGeoEventComponentName, isGeoEventServiceComponent, isInputComponent, isOutputComponent } from '../types/index.js';
import {
  buildVelocityModel,
  getAnalyticForService,
  buildVelocityComponentTable,
  buildAdditionalVelocityComponentTables,
  type VelocityModel,
} from './velocity-model.js';
import { buildVelocityFlowGraph, buildVelocityNeighborhoodGraph } from './velocity-flow.js';
import type { VelocityRealtimeAnalytic } from '../types/velocity.js';
import { sanitizeVelocityLabel } from '../generator/velocity-label.js';
import paramLabelMapRaw from '../mapper/parameter-labels.json';

/** Lookup map from internal GeoEvent property names to human-readable labels */
const paramLabelMap: Record<string, string> = paramLabelMapRaw as Record<string, string>;

/**
 * Velocity tools that append PascalCase fields to the output schema.
 * Standard (non-spatiotemporal) feature layers require all-lowercase field names,
 * so these tools need either a downstream Map Fields tool or a Spatiotemporal
 * Data Store output to avoid FIELD_NAMES_MUST_BE_LOWERCASE validation errors.
 */
const PASCAL_CASE_FIELD_TOOLS = new Set([
  'motion-calculator',
  'detect-gaps',
  'incident-detector',
  'detect-idle',
  'reconstruct-tracks',
  'find-dwell-locations',
  'journey-calculator',
  'distance-to-feature-calculator',
]);

/**
 * Checks if a GeoEvent output component targets a feature layer.
 * Mirrors the logic in json-generator's getOutputTypeName that decides
 * whether to inject a Map Fields tool for in-place migrations.
 */
function isFeatureLayerOutput(output: Output): boolean {
  const connectorId = output.connector || '';
  // Outputs whose connector includes '-out-' are outputs; those with
  // fs-add, bds-add, or FeatureService transport are feature layer targets.
  if (!connectorId.includes('-out-')) return false;
  if (connectorId.includes('fs-add') || connectorId.includes('bds-add') ||
      connectorId.includes('fs-update') || connectorId.includes('bds-update')) {
    return true;
  }
  const transportUri = output.transport?.uri || '';
  return transportUri.includes('FeatureService');
}

/**
 * Identifies source, processing, and sink nodes that cannot be started in a
 * Velocity analytic until their missing pipeline connection is resolved.
 */
interface ServiceTopologyAssessment {
  disconnectedNodeIds: Set<string>;
  disconnectedToolNodeIds: Set<string>;
  hasWorkingPipeline: boolean;
  hasValidationBlockingTopology: boolean;
}

function assessServiceTopology(graph: ServiceGraph): ServiceTopologyAssessment {
  const incoming = new Set(graph.edges.map((edge) => edge.to));
  const outgoing = new Set(graph.edges.map((edge) => edge.from));
  const downstream = new Map<string, string[]>();
  for (const edge of graph.edges) {
    const targets = downstream.get(edge.from) ?? [];
    targets.push(edge.to);
    downstream.set(edge.from, targets);
  }

  const reachable = new Set(graph.nodes.filter((node) => node.type === 'input').map((node) => node.id));
  const queue = [...reachable];
  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    for (const targetId of downstream.get(nodeId) ?? []) {
      if (!reachable.has(targetId)) {
        reachable.add(targetId);
        queue.push(targetId);
      }
    }
  }

  const disconnectedNodeIds = new Set(graph.nodes
    .filter((node) => {
      if (node.type === 'input') return !outgoing.has(node.id);
      if (node.type === 'output') return !incoming.has(node.id);
      return !incoming.has(node.id) || !outgoing.has(node.id);
    })
    .map((node) => node.id));
  const disconnectedToolNodeIds = new Set(graph.nodes
    .filter((node) =>
      disconnectedNodeIds.has(node.id) && (node.type === 'filter' || node.type === 'processor'))
    .map((node) => node.id));
  const hasWorkingPipeline = graph.nodes.some((node) => node.type === 'output' && reachable.has(node.id));

  return {
    disconnectedNodeIds,
    disconnectedToolNodeIds,
    hasWorkingPipeline,
    hasValidationBlockingTopology: !hasWorkingPipeline || disconnectedToolNodeIds.size > 0,
  };
}

function getValidationBlockingDisconnectedNodeIds(config: GeoEventConfiguration): Set<string> {
  const nodeIds = new Set<string>();
  for (const service of config.geoEventServices) {
    const topology = assessServiceTopology(buildServiceGraph(service, config));
    if (!topology.hasValidationBlockingTopology) continue;
    const blockingNodeIds = topology.hasWorkingPipeline
      ? topology.disconnectedToolNodeIds
      : topology.disconnectedNodeIds;
    for (const nodeId of blockingNodeIds) nodeIds.add(nodeId);
  }
  return nodeIds;
}

/**
 * Creates a URL-safe anchor ID from a component or service name
 */
function makeAnchorId(prefix: string, name: string): string {
  return prefix + '-' + name.replace(/[^a-zA-Z0-9-_]/g, '-').toLowerCase();
}

/**
 * Builds the "About This Guide" introduction section
 */
function buildIntroduction(targetPlatform: TargetPlatform): DocumentSection {
  const platformName =
    targetPlatform === 'velocity-online'
      ? 'ArcGIS Velocity for ArcGIS Online'
      : 'ArcGIS Velocity for ArcGIS Enterprise';

  const content =
    'This migration guide has been generated by the ArcGIS Velocity Migration Tool to assist ' +
    'administrators, GIS professionals, and contractors responsible for transitioning real-time event processing ' +
    `workflows from ArcGIS GeoEvent Server to ${platformName}.`;

  return {
    title: 'About This Guide',
    content,
    subsections: [
      {
        title: 'Purpose and Scope',
        content:
          'This guide provides a component-by-component analysis of your GeoEvent Server configuration, ' +
          'covering:\n\n' +
          '  - Inputs, outputs, and GeoEvent Services\n' +
          '  - Processor chains and data flow pipelines\n' +
          '  - Supporting elements such as GeoFences and ArcGIS Server connections\n\n' +
          'Use it as a reference from initial planning through post-migration validation.',
      },
      {
        title: 'How Components Are Evaluated',
        content:
          'Each component has been evaluated against a functional compatibility matrix and assigned one ' +
          'of three levels:\n\n' +
          '  - Fully Mapped: A direct Velocity equivalent exists with documented parameter differences.\n' +
          '  - Partially Mapped: An equivalent exists but requires manual work, such as rewriting filter ' +
          'expressions in Arcade, re-authenticating secured data sources, or addressing platform-specific ' +
          'networking constraints.\n' +
          '  - Unmapped: No Velocity equivalent exists. These are flagged for alternative approaches or ' +
          'Esri Professional Services engagement.',
      },
      {
        title: 'Working with the Generated Configuration',
        content:
          'The utility also generates a Velocity-compatible JSON file that can be imported through the Admin ' +
          'REST API. This file is a starting point, not a finished product. After import, expect:\n\n' +
          '  - Placeholder values marked "ACTION REQUIRED" that must be completed manually\n' +
          '  - Complex filter conditions and field calculations that need Arcade rewrites\n' +
          '  - Label adjustments made to comply with Velocity validation rules\n\n' +
          'Always import into a non-production environment first, validate each feed and analytic individually, ' +
          'and confirm end-to-end data flow before promoting to production.\n\n' +
          'If you just want the import steps, jump straight to ' +
          '[Importing the Velocity Configuration](#sec-importing-the-velocity-configuration).',
      },
    ],
  };
}

/**
 * Builds the executive summary section with items grouped by migration status
 */
function buildExecutiveSummary(
  config: GeoEventConfiguration,
  mappingResult: ConfigurationMappingResult,
  targetPlatform: TargetPlatform,
  options: MigrationDocumentOptions = {}
): DocumentSection {
  const platformName =
    targetPlatform === 'velocity-online'
      ? 'ArcGIS Velocity for ArcGIS Online'
      : 'ArcGIS Velocity for ArcGIS Enterprise';

  const totalComponents =
    config.inputs.length +
    config.outputs.length +
    config.geoEventServices.reduce((sum, s) => sum + s.nodes.length, 0);

  // Exclude service-level mappings from counts (service -> real-time analytic is a given)
  const componentMappings = mappingResult.mappings.filter(
    (m) => !isGeoEventServiceComponent(m.component)
  );
  const mappedCount = componentMappings.filter((m) => m.status === 'mapped').length;
  const partialCount = mappingResult.partialMappings.length;
  const unmappedCount = mappingResult.unmappedComponents.length;

  const content = [
    `This document provides a comprehensive migration guide for converting an ArcGIS GeoEvent Server configuration to ${platformName}.`,
    '',
    'Configuration Summary:',
    ...(options.sourceFileName ? [`Imported Configuration File: ${options.sourceFileName}`] : []),
    `GeoEvent Server Version: ${config.productVersion}`,
    `Total Inputs: ${config.inputs.length}`,
    `Total Outputs: ${config.outputs.length}`,
    `Total Services: ${config.geoEventServices.length}`,
    `Total GeoEvent Definitions: ${config.geoEventDefinitions.length}`,
    '',
    'Migration Status:',
    `Total Components: ${totalComponents}`,
    `Fully Mapped: ${mappedCount}`,
    `Partially Mapped: ${partialCount}`,
    `Requires Manual Configuration: ${unmappedCount}`,
  ].join('\n');

  // Build a lookup from component name to its mapping status
  const mappingByName = new Map<string, MappingResult>();
  for (const m of mappingResult.mappings) {
    if (isGeoEventServiceComponent(m.component)) continue;
    const componentName = getGeoEventComponentName(m.component);
    if (componentName) {
      mappingByName.set(componentName, m);
    }
  }

  // Categorise each component by migration status, with service components
  // nested under their parent service for scannable grouping
  type StatusCategory = 'unmapped' | 'partial' | 'mapped';
  interface LinkedItem { label: string; anchorId: string; children?: LinkedItem[]; }
  const buckets: Record<StatusCategory, LinkedItem[]> = {
    unmapped: [],
    partial: [],
    mapped: [],
  };

  function getStatus(name: string): StatusCategory {
    const mapping = mappingByName.get(name);
    if (!mapping || mapping.status === 'unmapped' || mapping.status === 'manual-required') return 'unmapped';
    if (mapping.status === 'partial') return 'partial';
    return 'mapped';
  }

  function getComponentLabel(name: string): string {
    const input = config.inputs.find((i) => i.name === name);
    if (input) return input.label || input.name;
    const output = config.outputs.find((o) => o.name === name);
    if (output) return output.label || output.name;
    // It's a service node — find it in any service
    for (const svc of config.geoEventServices) {
      const node = svc.nodes.find((n) => n.name === name);
      if (node) return node.label || node.name;
      const si = svc.inputs.find((i) => i.inputRef === name);
      if (si) return si.label || name;
      const so = svc.outputs.find((o) => o.outputRef === name);
      if (so) return so.label || name;
    }
    return name;
  }

  function getComponentType(name: string): string {
    if (config.inputs.some((i) => i.name === name)) return 'Input';
    if (config.outputs.some((o) => o.name === name)) return 'Output';
    for (const svc of config.geoEventServices) {
      const node = svc.nodes.find((n) => n.name === name);
      if (node) {
        if (node.filter) return 'Filter';
        if (node.processor) return 'Processor';
      }
    }
    return 'Component';
  }

  function getConnectorSuffix(name: string): string {
    const input = config.inputs.find((i) => i.name === name);
    if (input?.connectorLabel) return ` (${input.connectorLabel})`;
    const output = config.outputs.find((o) => o.name === name);
    if (output?.connectorLabel) return ` (${output.connectorLabel})`;
    // For processors and filters, use the mapping's geoEventComponentLabel
    const mapping = mappingByName.get(name);
    if (mapping?.mapping?.geoEventComponentLabel) return ` (${mapping.mapping.geoEventComponentLabel})`;
    return '';
  }

  // Track which components belong to a service so we can identify orphans
  const serviceComponentNames = new Set<string>();
  for (const service of config.geoEventServices) {
    for (const si of service.inputs) serviceComponentNames.add(si.inputRef);
    for (const node of service.nodes) serviceComponentNames.add(node.name);
    for (const so of service.outputs) serviceComponentNames.add(so.outputRef);
  }

  // Standalone inputs (not in a service)
  for (const input of config.inputs) {
    if (serviceComponentNames.has(input.name)) continue;
    const status = getStatus(input.name);
    const connector = input.connectorLabel ? ` (${input.connectorLabel})` : '';
    buckets[status].push({
      label: `Input: ${input.label || input.name}${connector}`,
      anchorId: makeAnchorId('comp', input.name),
    });
  }

  // Standalone outputs (not in a service)
  for (const output of config.outputs) {
    if (serviceComponentNames.has(output.name)) continue;
    const status = getStatus(output.name);
    const connector = output.connectorLabel ? ` (${output.connectorLabel})` : '';
    buckets[status].push({
      label: `Output: ${output.label || output.name}${connector}`,
      anchorId: makeAnchorId('comp', output.name),
    });
  }

  // Service components — group children under the service name
  for (const service of config.geoEventServices) {
    const serviceLabel = service.label || service.name;
    const componentNames: string[] = [];
    for (const si of service.inputs) componentNames.push(si.inputRef);
    for (const node of service.nodes) componentNames.push(node.name);
    for (const so of service.outputs) componentNames.push(so.outputRef);

    // Group the service's components by their individual status
    const childBuckets: Record<StatusCategory, LinkedItem[]> = {
      unmapped: [],
      partial: [],
      mapped: [],
    };
    for (const compName of componentNames) {
      const status = getStatus(compName);
      const compLabel = getComponentLabel(compName);
      const compType = getComponentType(compName);
      const connector = getConnectorSuffix(compName);
      childBuckets[status].push({
        label: `${compType}: ${compLabel}${connector}`,
        anchorId: makeAnchorId('comp', compName),
      });
    }

    // Add the service as a parent item in each bucket that has children
    for (const status of ['unmapped', 'partial', 'mapped'] as StatusCategory[]) {
      if (childBuckets[status].length > 0) {
        buckets[status].push({
          label: `Service: ${serviceLabel}`,
          anchorId: makeAnchorId('svc', service.name),
          children: childBuckets[status],
        });
      }
    }
  }

  // Build subsections ordered: incompatible first, then partial, then fully mapped
  const subsections: DocumentSection[] = [];

  // Count leaf items (children or top-level items without children)
  function countLeafItems(items: LinkedItem[]): number {
    return items.reduce((sum, item) => sum + (item.children ? item.children.length : 1), 0);
  }

  if (buckets.unmapped.length > 0) {
    subsections.push({
      title: 'Requires Manual Configuration',
      content: `${countLeafItems(buckets.unmapped)} component(s) have no direct Velocity equivalent and require manual configuration or alternative approaches.`,
      linkedItems: buckets.unmapped,
    });
  }

  if (buckets.partial.length > 0) {
    subsections.push({
      title: 'Partially Compatible',
      content: `${countLeafItems(buckets.partial)} component(s) have a Velocity equivalent but require manual steps such as Arcade rewrites or re-authentication.`,
      linkedItems: buckets.partial,
    });
  }

  if (buckets.mapped.length > 0) {
    subsections.push({
      title: 'Fully Compatible',
      content: `${countLeafItems(buckets.mapped)} component(s) have a direct Velocity equivalent.`,
      linkedItems: buckets.mapped,
    });
  }

  return {
    title: 'Executive Summary',
    content,
    subsections,
  };
}

/**
 * Builds the service-centric migration details section.
 * Each GeoEvent Service becomes a subsection containing its flow diagram
 * and all component mappings (inputs, processors, filters, outputs) nested
 * under the service they belong to, with compatibility flags.
 */
function buildServiceMigrationDetails(
  config: GeoEventConfiguration,
  mappingResult: ConfigurationMappingResult,
  diagrams: Map<string, MermaidDiagram>,
  securedInputNames: Set<string> = new Set(),
  targetPlatform: TargetPlatform = 'velocity-online',
  arcadeNotesLookup: Map<string, ArcadeNoteRow[]> = new Map(),
  migrationStrategy: MigrationStrategy = 'side-by-side',
  velocityModel?: VelocityModel,
): DocumentSection {
  // Build a lookup from component name/ref to its MappingResult
  const mappingByName = new Map<string, MappingResult>();
  for (const m of mappingResult.mappings) {
    if (isGeoEventServiceComponent(m.component)) continue;
    const componentName = getGeoEventComponentName(m.component);
    if (componentName) {
      mappingByName.set(componentName, m);
    }
  }

  // Track which components are referenced by at least one service
  const referencedComponents = new Set<string>();
  const validationBlockingDisconnectedNodeIds = getValidationBlockingDisconnectedNodeIds(config);

  const serviceSubsections: DocumentSection[] = [];

  for (const service of config.geoEventServices) {
    const serviceLabel = service.label || service.name;
    const diagram = diagrams.get(service.name);
    const serviceGraph = diagram?.graph;
    const topology = serviceGraph ? assessServiceTopology(serviceGraph) : undefined;

    // The Velocity real-time analytic generated for this service (when
    // available) drives the Velocity-side configuration tables.
    const analytic = velocityModel ? getAnalyticForService(velocityModel, service) : undefined;

    // Build a flat list of component sections in topological (data flow) order.
    // This follows Input -> Nodes -> Output naturally based on the service graph.
    const componentSections: DocumentSection[] = [];

    // Build a set of all component IDs in this service for quick lookup
    const serviceComponentIds = new Set<string>();
    for (const si of service.inputs) serviceComponentIds.add(si.inputRef);
    for (const node of service.nodes) serviceComponentIds.add(node.name);
    for (const so of service.outputs) serviceComponentIds.add(so.outputRef);

    // Use topological sort if we have a graph, otherwise fall back to
    // inputs -> nodes -> outputs order
    const orderedIds: string[] = [];
    if (serviceGraph) {
      const sortResult = topologicalSort(serviceGraph);
      for (const graphNode of sortResult.order) {
        if (serviceComponentIds.has(graphNode.id)) {
          orderedIds.push(graphNode.id);
        }
      }
      // Add any IDs not in the graph (shouldn't happen, but be safe)
      for (const id of serviceComponentIds) {
        if (!orderedIds.includes(id)) orderedIds.push(id);
      }
    } else {
      // Fallback: inputs, then nodes, then outputs
      for (const si of service.inputs) orderedIds.push(si.inputRef);
      for (const node of service.nodes) orderedIds.push(node.name);
      for (const so of service.outputs) orderedIds.push(so.outputRef);
    }

    // Build a section for each component in topological order
    for (const compId of orderedIds) {
      referencedComponents.add(compId);
      const mapping = mappingByName.get(compId);
      // Per-component "Node Neighbor(s)" diagrams are disabled. With the
      // interactive service/analytic diagrams now in the HTML guide, the
      // per-component neighborhood charts (and their "Node Neighbor(s)" title)
      // became redundant — "death by flow chart". The building/rendering code
      // is intentionally left intact; flip this flag back to true to restore
      // the feature (renderDiagram in html-generator.ts still renders them).
      const ENABLE_NEIGHBOR_DIAGRAMS: boolean = false;
      const neighborhood = ENABLE_NEIGHBOR_DIAGRAMS && serviceGraph
        ? buildNeighborhoodDiagram(serviceGraph, compId)
        : undefined;
      const compAnchorId = makeAnchorId('comp', compId);

      if (mapping) {
        // Attach a matching Velocity-side neighborhood so the per-component
        // diagram offers a "Velocity" view alongside the GeoEvent one, driven
        // by the global toggle.
        if (neighborhood && velocityModel && analytic) {
          const geoLabel = getGeoEventComponentLabel(mapping.component) || getComponentName(mapping.component);
          const vn = buildVelocityNeighborhoodGraph(velocityModel, analytic, geoLabel);
          if (vn) {
            neighborhood.velocityGraph = vn.graph;
            neighborhood.velocityHighlightNodeId = vn.highlightNodeId;
          }
        }
        const topologyNote = validationBlockingDisconnectedNodeIds.has(compId)
          ? 'Validation: This component is disconnected in at least one analytic and is preventing proper validation. Connect or remove it in each affected analytic before starting the analytic.'
          : topology?.disconnectedNodeIds.has(compId)
          ? !topology.hasWorkingPipeline
            ? 'Validation: This component is disconnected and the analytic has no complete feed-to-output pipeline. Connect or remove it and ensure at least one feed reaches an output before starting the analytic.'
            : topology.disconnectedToolNodeIds.has(compId)
              ? 'Validation: This tool is disconnected. Velocity rejects analytics that contain disconnected tools, even when another feed-to-output pipeline is complete. Connect or remove this tool before starting the analytic.'
              : 'Validation: This component is disconnected and is not used for processing in this analytic. Velocity will report a warning; connect it or remove it if it is not intended.'
          : undefined;
        const section = formatComponentForService(mapping, neighborhood, config.arcgisServerConnections, securedInputNames, targetPlatform, arcadeNotesLookup, config.folderDataStores, migrationStrategy, config, velocityModel, analytic, topologyNote);
        section.anchorId = compAnchorId;
        componentSections.push(section);
      } else {
        // No mapping — build a basic section from the raw component data
        const fullInput = config.inputs.find((i) => i.name === compId);
        const fullOutput = config.outputs.find((o) => o.name === compId);
        const node = service.nodes.find((n) => n.name === compId);
        const svcInput = service.inputs.find((si) => si.inputRef === compId);
        const svcOutput = service.outputs.find((so) => so.outputRef === compId);

        const label = fullInput?.label || fullOutput?.label || node?.label
          || svcInput?.label || svcOutput?.label || compId;
        const connector = fullInput?.connectorLabel || fullOutput?.connectorLabel || '';
        const rawComponent = fullInput || fullOutput || node;
        const rawName = (rawComponent as { name?: string } | undefined)?.name;
        const rawAgolResolution = rawName ? config.agolResolutions?.[rawName] : undefined;
        const tables = rawComponent ? extractComponentTables(rawComponent, config.arcgisServerConnections, new Map(), config.folderDataStores, rawAgolResolution) : undefined;

        // Unmapped components rarely have a Velocity equivalent, but attach one
        // when the generator produced a correlated Velocity component anyway.
        if (neighborhood && velocityModel && analytic) {
          const vn = buildVelocityNeighborhoodGraph(velocityModel, analytic, label);
          if (vn) {
            neighborhood.velocityGraph = vn.graph;
            neighborhood.velocityHighlightNodeId = vn.highlightNodeId;
          }
        }

        componentSections.push({
          title: label,
          content: connector
            ? `Connector: ${connector}\nStatus: No mapping available`
            : 'Status: No mapping available',
          diagram: neighborhood,
          tables: tables && tables.length > 0 ? tables : undefined,
          anchorId: compAnchorId,
          metadata: {
            kind: 'component',
            status: 'unmapped',
            componentType: node?.filter
              ? 'filter'
              : node?.processor
                ? 'processor'
                : fullInput
                  ? 'input'
                  : fullOutput
                    ? 'output'
                    : undefined,
            searchTerms: [label, connector, compId]
              .filter((v): v is string => typeof v === 'string' && v.length > 0)
              .map((v) => v.toLowerCase()),
          },
        });
      }
    }

    // --- Additional Velocity Components (no GeoEvent origin) ---
    // Velocity sometimes requires tools/sources the GeoEvent configuration did
    // not contain (e.g. an injected Map Fields tool for in-place feature layer
    // outputs, or an enrichment processor split out into a standalone data
    // source). Surface these so the guide reflects the full Velocity model.
    if (velocityModel && analytic) {
      const serviceGeoEventLabels: string[] = [];
      for (const si of service.inputs) {
        const gi = config.inputs.find((i) => i.name === si.inputRef);
        serviceGeoEventLabels.push(gi?.label || si.label || si.inputRef);
      }
      for (const node of service.nodes) {
        serviceGeoEventLabels.push(node.label || node.name);
      }
      for (const so of service.outputs) {
        const go = config.outputs.find((o) => o.name === so.outputRef);
        serviceGeoEventLabels.push(go?.label || so.label || so.outputRef);
      }

      const additionalTables = buildAdditionalVelocityComponentTables(
        velocityModel,
        analytic,
        serviceGeoEventLabels,
      );
      if (additionalTables.length > 0) {
        componentSections.push({
          title: 'Additional Velocity Components',
          content:
            'The Velocity model for this service includes the following component(s) that have no ' +
            'direct GeoEvent Server equivalent. The ArcGIS Velocity Migration Tool added them so the ' +
            'generated configuration is valid and behaves like the original — for example, a Map Fields ' +
            'tool to reconcile field types, or an enrichment processor expressed as a standalone data source. ' +
            'Review their settings after import.',
          tables: additionalTables,
          metadata: {
            kind: 'component',
            searchTerms: ['additional velocity components', 'map fields', 'data source'],
          },
        });
      }
    }

    // Build summary counts for the service header
    const allServiceMappings = orderedIds
      .map((id) => mappingByName.get(id))
      .filter(Boolean) as MappingResult[];

    const fullyMapped = allServiceMappings.filter((m) => m.status === 'mapped').length;
    const needsReview = allServiceMappings.length - fullyMapped;

    const summaryLines: string[] = [];
    if (service.description) {
      summaryLines.push(`Description: ${service.description}`);
    }
    summaryLines.push(`Velocity Equivalent: Real-Time Analytic`);
    summaryLines.push(`Components: ${allServiceMappings.length} total`);
    if (fullyMapped > 0) {
      summaryLines.push(`Fully Compatible: ${fullyMapped}`);
    }
    if (needsReview > 0) {
      summaryLines.push(`Needs Review: ${needsReview}`);
    }

    // Attach the Velocity-side flow graph so the HTML guide can offer a
    // "Velocity model" view of the interactive diagram. Only when both a
    // GeoEvent diagram and a matching Velocity analytic exist.
    let serviceDiagram = diagram;
    if (diagram && velocityModel && analytic) {
      const velocityGraph = buildVelocityFlowGraph(velocityModel, analytic);
      if (velocityGraph.nodes.length > 0) {
        // Flag Velocity nodes whose GeoEvent origin needs manual work
        // (status manual-required, unmapped, or partial) so the guide can
        // outline them in red. Only fully `mapped` components need no manual
        // work — everything else requires some hands-on setup.
        const manualLabels = new Set<string>();
        for (const m of allServiceMappings) {
          if (m.status === 'manual-required' || m.status === 'unmapped' || m.status === 'partial') {
            const geoLabel = getGeoEventComponentLabel(m.component) || getComponentName(m.component);
            manualLabels.add(sanitizeVelocityLabel(geoLabel).sanitized);
          }
        }
        for (const nodeId of topology?.disconnectedNodeIds ?? []) {
          const mapping = mappingByName.get(nodeId);
          const geoLabel = mapping
            ? getGeoEventComponentLabel(mapping.component) || getComponentName(mapping.component)
            : serviceGraph?.nodes.find((node) => node.id === nodeId)?.label;
          if (geoLabel) manualLabels.add(sanitizeVelocityLabel(geoLabel).sanitized);
        }
        const velocityManualNodeIds = manualLabels.size > 0
          ? velocityGraph.nodes.filter((n) => manualLabels.has(n.label)).map((n) => n.id)
          : [];
        serviceDiagram = {
          ...diagram,
          velocityGraph,
          ...(velocityManualNodeIds.length > 0 ? { velocityManualNodeIds } : {}),
        };
      }
    }

    serviceSubsections.push({
      title: serviceLabel,
      content: summaryLines.join('\n'),
      anchorId: makeAnchorId('svc', service.name),
      diagram: serviceDiagram,
      subsections: componentSections,
      metadata: {
        kind: 'service',
        searchTerms: [serviceLabel, service.name, service.description ?? '']
          .filter((v) => v && v.length > 0)
          .map((v) => v.toLowerCase()),
      },
    });
  }

  // --- Orphaned components (not referenced by any service) ---
  const orphanedMappings = mappingResult.mappings.filter((m) => {
    if (isGeoEventServiceComponent(m.component)) return false;
    const componentName = getGeoEventComponentName(m.component);
    return componentName ? !referencedComponents.has(componentName) : true;
  });

  if (orphanedMappings.length > 0) {
    const orphanGroups: DocumentSection[] = [];
    const orphanInputs = orphanedMappings.filter((m) => isInputComponent(m.component));
    const orphanOutputs = orphanedMappings.filter((m) => isOutputComponent(m.component));
    const orphanOther = orphanedMappings.filter((m) => !isInputComponent(m.component) && !isOutputComponent(m.component));

    if (orphanInputs.length > 0) {
      orphanGroups.push({
        title: 'Inputs',
        content: '',
        subsections: orphanInputs.map((m) => formatComponentForService(m, undefined, config.arcgisServerConnections, securedInputNames, targetPlatform, arcadeNotesLookup, config.folderDataStores, migrationStrategy, config)),
      });
    }
    if (orphanOutputs.length > 0) {
      orphanGroups.push({
        title: 'Outputs',
        content: '',
        subsections: orphanOutputs.map((m) => formatComponentForService(m, undefined, config.arcgisServerConnections, securedInputNames, targetPlatform, arcadeNotesLookup, config.folderDataStores, migrationStrategy, config)),
      });
    }
    if (orphanOther.length > 0) {
      orphanGroups.push({
        title: 'Other Components',
        content: '',
        subsections: orphanOther.map((m) => formatComponentForService(m, undefined, config.arcgisServerConnections, securedInputNames, targetPlatform, arcadeNotesLookup, config.folderDataStores, migrationStrategy, config)),
      });
    }

    serviceSubsections.push({
      title: 'Components Not Assigned to a Service',
      content: 'The following components were found in the configuration but are not referenced by any GeoEvent Service.',
      subsections: orphanGroups,
    });
  }

  return {
    title: 'Service Migration Details',
    content: 'This section details each GeoEvent Service and its components, mapped to their Velocity equivalents.',
    subsections: serviceSubsections,
  };
}

/**
 * Builds a neighborhood subgraph for a given node, containing the node itself
 * plus all immediate predecessors (nodes with edges into it) and successors
 * (nodes it has edges to). Returns a MermaidDiagram for the subgraph,
 * or undefined if the graph has fewer than 3 nodes (neighborhood would be trivial).
 */
function buildNeighborhoodDiagram(
  graph: ServiceGraph,
  nodeId: string
): MermaidDiagram | undefined {
  // Don't bother with neighborhood diagrams for very small graphs
  if (graph.nodes.length <= 3) return undefined;

  // Find predecessor and successor node IDs
  const predecessors = new Set<string>();
  const successors = new Set<string>();
  for (const edge of graph.edges) {
    if (edge.to === nodeId) predecessors.add(edge.from);
    if (edge.from === nodeId) successors.add(edge.to);
  }

  // Collect the neighborhood node IDs
  const neighborIds = new Set<string>([nodeId, ...predecessors, ...successors]);

  // If the neighborhood is the entire graph, skip (the full diagram already shows it)
  if (neighborIds.size >= graph.nodes.length) return undefined;

  // Build subgraph
  const subNodes = graph.nodes.filter((n) => neighborIds.has(n.id));
  const subEdges = graph.edges.filter(
    (e) => neighborIds.has(e.from) && neighborIds.has(e.to)
  );

  const subGraph: ServiceGraph = { nodes: subNodes, edges: subEdges };
  const diagram = renderServiceGraph(subGraph);
  diagram.highlightNodeId = nodeId;
  return diagram;
}

function buildCustomComponentNote(component: unknown, config?: GeoEventConfiguration): string {
  if (!config || typeof component !== 'object' || component === null) return '';

  const comp = component as Record<string, unknown>;
  if (!('adapter' in comp) || !('transport' in comp)) return '';

  const adapter = comp.adapter as { uri?: string } | undefined;
  const transport = comp.transport as { uri?: string } | undefined;
  const analysis = detectCustomComponents(config);
  const customAdapterUris = new Set(analysis.customAdapters.map((entry) => entry.uri));
  const customTransportUris = new Set(analysis.customTransports.map((entry) => entry.uri));
  const esriAddonAdapterUris = new Set(analysis.esriAddonAdapters.map((entry) => entry.uri));
  const esriAddonTransportUris = new Set(analysis.esriAddonTransports.map((entry) => entry.uri));
  const hasCustomAdapter = !!adapter?.uri && customAdapterUris.has(adapter.uri);
  const hasCustomTransport = !!transport?.uri && customTransportUris.has(transport.uri);
  const hasEsriAddonAdapter = !!adapter?.uri && esriAddonAdapterUris.has(adapter.uri);
  const hasEsriAddonTransport = !!transport?.uri && esriAddonTransportUris.has(transport.uri);

  if (!hasCustomAdapter && !hasCustomTransport && !hasEsriAddonAdapter && !hasEsriAddonTransport) return '';

  const connector = typeof comp.connector === 'string' ? comp.connector : '';
  const componentKind = connector.includes('-out-') ? 'output' : 'input';
  if (hasEsriAddonAdapter || hasEsriAddonTransport) {
    return `This ${componentKind} uses an Esri-built GeoEvent Server add-on that is not part of the core COTS product. ` +
      'Assess its Velocity equivalency separately from the core GeoEvent Server baseline.';
  }
  const customDescription = hasCustomAdapter && hasCustomTransport
    ? 'custom GeoEvent Server adapter and transport components'
    : hasCustomAdapter
      ? 'a custom GeoEvent Server adapter component'
      : 'a custom GeoEvent Server transport component';

  return `This ${componentKind} includes ${customDescription} that will need to be rebuilt for the target Velocity architecture. ` +
    'No direct Velocity equivalent exists. This component requires evaluation for alternative approaches.';
}

/**
 * Maps a MappingResult.status to the normalised
 * {@link DocumentSectionStatus} used by the interactive HTML toolbar.
 */
function toSectionStatus(status: MappingResult['status']): DocumentSectionStatus {
  if (status === 'mapped' || status === 'partial' || status === 'unmapped' || status === 'manual-required') {
    return status;
  }
  return 'unmapped';
}

/**
 * Infers the component-type filter value for a mapping. Inputs and
 * outputs are obvious; for service nodes we inspect whether the node
 * carries a `filter` or `processor` payload.
 */
function inferComponentType(component: unknown): DocumentSectionComponentType | undefined {
  if (isInputComponent(component)) return 'input';
  if (isOutputComponent(component)) return 'output';
  if (typeof component === 'object' && component !== null) {
    const obj = component as Record<string, unknown>;
    if ('filter' in obj && obj.filter) return 'filter';
    if ('processor' in obj && obj.processor) return 'processor';
  }
  return undefined;
}

/**
 * Builds an array of additional lower-case search terms for a
 * component-kind section so the HTML toolbar's search can match on
 * connector labels, GeoEvent labels, and mapping target labels
 * without having to traverse inner DOM text on every keystroke.
 */
function buildComponentSearchTerms(mapping: MappingResult): string[] {
  const terms: string[] = [];
  const push = (v: unknown): void => {
    if (typeof v === 'string' && v.trim().length > 0) terms.push(v.trim().toLowerCase());
  };
  const obj = mapping.component as unknown as Record<string, unknown>;
  push(obj?.name);
  push(obj?.label);
  push(obj?.connectorLabel);
  push(obj?.connector);
  push(mapping.mapping?.geoEventComponentLabel);
  push(mapping.mapping?.velocityComponentLabel);
  push(mapping.mapping?.geoEventType);
  return Array.from(new Set(terms));
}

/**
 * Formats a single mapping result for display within a service context,
 * including a compatibility status flag, neighborhood flow diagram, and all parameter details.
 */
function formatComponentForService(
  mapping: MappingResult,
  neighborhoodDiagram?: MermaidDiagram,
  connections?: ArcGISServerConnection[],
  securedInputNames: Set<string> = new Set(),
  targetPlatform: TargetPlatform = 'velocity-online',
  arcadeNotesLookup: Map<string, ArcadeNoteRow[]> = new Map(),
  folderDataStores: FolderDataStore[] = [],
  migrationStrategy: MigrationStrategy = 'side-by-side',
  config?: GeoEventConfiguration,
  velocityModel?: VelocityModel,
  analytic?: VelocityRealtimeAnalytic,
  topologyNote?: string,
): DocumentSection {
  const componentName = getComponentName(mapping.component);
  const lines: string[] = [];
  const customComponentNote = buildCustomComponentNote(mapping.component, config);

  // Compatibility status flag
  const statusLabel = mapping.status === 'mapped'
    ? 'Fully Compatible'
    : mapping.status === 'partial'
      ? 'Needs Review - Partially Compatible'
      : 'Needs Review - Manual Configuration Required';
  lines.push(`Status: ${statusLabel}`);
  if (topologyNote) lines.push(topologyNote);

  if (mapping.mapping) {
    lines.push(`GeoEvent Type: ${mapping.mapping.geoEventType}`);
    lines.push(`GeoEvent Component: ${mapping.mapping.geoEventComponentLabel}`);
    lines.push(`Velocity Equivalent: ${mapping.mapping.velocityComponentLabel}`);

    if (mapping.mapping.geoEventHelpUrl) {
      lines.push(`GeoEvent Server Help: ${mapping.mapping.geoEventHelpUrl}`);
    }

    if (mapping.mapping.geoEventGalleryItemUrl) {
      const deprecatedSuffix = mapping.mapping.geoEventGalleryItemDeprecated
        ? ' (DEPRECATED on ArcGIS Online — the GeoEvent gallery item is no longer recommended for new deployments)'
        : '';
      lines.push(`GeoEvent Gallery Item: ${mapping.mapping.geoEventGalleryItemUrl}${deprecatedSuffix}`);
    }

    if (mapping.mapping.velocityHelpUrl) {
      lines.push(`Velocity Help: ${mapping.mapping.velocityHelpUrl}`);
    }

    const notes = [mapping.mapping.notes, customComponentNote].filter(Boolean).join(' ');
    if (notes) {
      lines.push(`Notes: ${notes}`);
    }

    if (mapping.mapping.limitations.length > 0) {
      lines.push(`Limitations: ${mapping.mapping.limitations.join(', ')}`);
    }

    if (mapping.mapping.migrationSteps && mapping.mapping.migrationSteps.length > 0) {
      // Determine whether automated steps actually took effect for this
      // component. An auto step is only truthfully "done" when the
      // component made it into the generated Velocity JSON AND the
      // input binding is viable.
      const inputName = getGeoEventComponentName(mapping.component) || '';
      const isInJson = mapping.status !== 'unmapped';
      const isSecured = securedInputNames.has(inputName);
      const agolRes = config?.agolResolutions?.[inputName];
      const isAgolUnresolved =
        !!agolRes && agolRes.status !== 'resolved' && agolRes.status !== 'not-applicable';
      const bindingBlocked = isSecured || isAgolUnresolved;

      let blockerNote = '';
      if (!isInJson) {
        blockerNote =
          'No automated steps were executed because this component has no Velocity equivalent ' +
          'and is not present in the generated Velocity JSON.';
      } else if (bindingBlocked) {
        const reasons: string[] = [];
        if (isSecured) reasons.push('the source service is secured / not anonymously accessible');
        if (isAgolUnresolved) reasons.push('the ArcGIS Online–hosted service could not be auto-resolved to a public Portal item');
        blockerNote =
          `Automated values were written to the Velocity JSON, but the feed will not run until the binding is resolved (${reasons.join('; ')}). ` +
          'Complete the manual steps above (wrap the service in a Portal item with stored credentials and bind it via portalItemId) before treating the auto-configured values as in effect.';
      }

      lines.push('');
      lines.push('Migration Steps:');
      // Inspect what the converter produced for this component's Arcade
      // expression(s). The arcadeNotesLookup keys on the node label.
      //   - "confident": at least one row, none with ACTION REQUIRED / VERIFY
      //   - "needsManual": one or more rows tagged ACTION REQUIRED — the
      //     converter could not produce a working expression and the user
      //     must rewrite it by hand.
      const componentLabel = getGeoEventComponentLabel(mapping.component) || '';
      const arcadeRowsForComponent = arcadeNotesLookup.get(componentLabel) || [];
      const hasConfidentArcade =
        arcadeRowsForComponent.length > 0 &&
        arcadeRowsForComponent.every(
          (r) =>
            !r.expression.includes('ACTION REQUIRED') &&
            !r.expression.includes('VERIFY'),
        );
      const needsManualArcade = arcadeRowsForComponent.some((r) =>
        r.expression.includes('ACTION REQUIRED'),
      );
      const hasVerifyArcade = arcadeRowsForComponent.some(
        (r) => r.expression.includes('VERIFY') && !r.expression.includes('ACTION REQUIRED'),
      );
      const arcadeHelpLinks =
        '\n      - Velocity Arcade profile: https://developers.arcgis.com/arcade/profiles/velocity/' +
        '\n      - Arcade Playground (interactive): https://developers.arcgis.com/arcade/playground/' +
        '\n      - Using Arcade in Velocity: https://doc.arcgis.com/en/iot/analyze/use-arcade-expressions.htm';

      // Detect the matrix's "Rewrite/Convert ... expression" rewrite step.
      // When the component is in JSON, binding is viable, and the converter
      // did NOT flag ACTION REQUIRED, we replace the matrix step list with
      // an honest two-step sequence: confirm auto-configuration + verify
      // results. The original matrix steps still apply when:
      //   - the component never reached the JSON, or
      //   - the binding is blocked (secured/AGOL unresolved), or
      //   - the converter flagged ACTION REQUIRED (manual rewrite needed).
      const rewriteStepInMatrix = mapping.mapping.migrationSteps.find(
        (s) =>
          s.manual &&
          /Rewrite GeoEvent Server expression|Convert GeoEvent filter expression/i.test(s.action),
      );
      const autoConfiguredCleanly =
        !!rewriteStepInMatrix && isInJson && !bindingBlocked && !needsManualArcade;

      if (autoConfiguredCleanly) {
        // Extract any /* VERIFY: ... */ comment text from the generated
        // Arcade so the doc surfaces the same warning the user will see
        // inside the Velocity expression builder.
        const verifyNotes: string[] = [];
        if (hasVerifyArcade) {
          for (const row of arcadeRowsForComponent) {
            const m = row.expression.match(/\/\*\s*VERIFY:([\s\S]*?)\*\//);
            if (m && m[1]) {
              const cleaned = m[1]
                .split(/\r?\n/)
                .map((l) => l.trim().replace(/^\*\s?/, ''))
                .filter((l) => l.length > 0)
                .join(' ');
              if (cleaned && !verifyNotes.includes(cleaned)) {
                verifyNotes.push(cleaned);
              }
            }
          }
        }

        const componentDisplay = mapping.mapping.velocityComponentLabel || 'Velocity tool';
        const autoLine =
          `  [x] Step 1: ${componentDisplay} was auto-configured from matrix mapping ` +
          `(GeoEvent expression translated to Arcade and written into the generated Velocity JSON).`;
        lines.push(autoLine);
        if (verifyNotes.length > 0) {
          for (const note of verifyNotes) {
            lines.push(`      - VERIFY (from generated Arcade): ${note}`);
          }
        }
        lines.push(
          `  [ ] Step 2: Verify the auto-configured behavior produces the expected results in Velocity. ` +
            `Refine the expression in the Velocity expression builder or the Arcade Playground if needed.` +
            arcadeHelpLinks,
        );
      } else {
        for (const step of mapping.mapping.migrationSteps) {
          // The "Rewrite GeoEvent Server expression…" step is declared as
          // manual in the matrix, but its actual state depends on what the
          // converter produced.
          const isRewriteStep =
            step.manual && /Rewrite GeoEvent Server expression/i.test(step.action);
          if (isRewriteStep && isInJson && !bindingBlocked) {
            if (hasConfidentArcade) {
              // Working expression generated — mark complete with verify guidance.
              lines.push(
                `  [x] Step ${step.step}: ${step.action} ` +
                  `(auto-converted by the ArcGIS Velocity Migration Tool — manually verify the generated Arcade expression meets your requirements before going live; ` +
                  `refine in the Velocity expression builder or Arcade Playground if needed)` +
                  arcadeHelpLinks,
              );
            } else if (needsManualArcade) {
              // ACTION REQUIRED comments present — explicit manual rewrite needed.
              lines.push(
                `  [ ] Step ${step.step}: ${step.action} ` +
                  `(ACTION REQUIRED — the ArcGIS Velocity Migration Tool could not auto-convert this expression; ` +
                  `you must manually rewrite the GeoEvent Server expression in Arcade. ` +
                  `Plan additional small (S) relative effort for this component — see the Level of Effort Estimate for the scale)` +
                  arcadeHelpLinks,
              );
            } else {
              lines.push(`  [ ] Step ${step.step}: ${step.action}`);
            }
            continue;
          }

          if (step.manual) {
            lines.push(`  [ ] Step ${step.step}: ${step.action}`);
          } else if (!isInJson) {
            // Component never reached the JSON — claim nothing.
            lines.push(`  [ ] Step ${step.step}: ${step.action} (not auto-configured — component not present in generated Velocity JSON)`);
          } else if (bindingBlocked) {
            // Value is in JSON but feed is non-functional until manual steps complete.
            const suffix = step.autoValue
              ? ` (value written to JSON: ${step.autoValue}, but pending — see note below)`
              : ' (value written to JSON, but pending — see note below)';
            lines.push(`  [~] Step ${step.step}: ${step.action}${suffix}`);
          } else {
            const suffix = step.autoValue ? ` (auto-configured: ${step.autoValue})` : ' (auto-configured)';
            lines.push(`  [x] Step ${step.step}: ${step.action}${suffix}`);
          }
        }
      }
      if (blockerNote) {
        lines.push('');
        lines.push(`Note on auto-configured steps: ${blockerNote}`);
      }
    }

    if (PASCAL_CASE_FIELD_TOOLS.has(mapping.mapping.velocityComponent)) {
      lines.push('');
      lines.push(
        'Important: This Velocity tool appends PascalCase fields to the output schema ' +
        '(e.g. "Speed", "IsGap", "IncidentId"). Standard feature layers require all-lowercase ' +
        'field names and will reject these fields during validation. To resolve this, either ' +
        'add a Map Fields tool immediately after this component to rename the PascalCase fields ' +
        'to lowercase, or configure the output to write to the Spatiotemporal Data Store which ' +
        'supports case-sensitive field names.'
      );
    }
  } else if (customComponentNote) {
    lines.push(`Notes: ${customComponentNote}`);
  }

  // Detect secured feature layer connections for input/output components
  if (isInputComponent(mapping.component) || isOutputComponent(mapping.component)) {
    const inputName = getGeoEventComponentName(mapping.component) || '';
    if (securedInputNames.has(inputName)) {
      lines.push('');
      lines.push(`Action Required — Configure Secured Service Binding: ${getSecuredFeatureLayerNote(targetPlatform)}`);
    }

    // For in-place migration, note that a Map Fields tool has been injected
    // before feature layer outputs to convert Int64 fields to Int32
    if (migrationStrategy === 'in-place' && isOutputComponent(mapping.component) && isFeatureLayerOutput(mapping.component)) {
      lines.push('');
      lines.push(
        'Important: A Map Fields tool has been added before this output in the generated Velocity configuration. ' +
        'GeoEvent Server Integer and Long field types map to Velocity Int64, but existing feature layers typically ' +
        'use esriFieldTypeInteger (Int32). The Map Fields tool converts these fields using Arcade Number() expressions ' +
        'to ensure compatibility with the existing feature layer schema. Review the field mappings after import and ' +
        'adjust if your feature layer uses non-standard field types.'
      );
    }
  }

  const componentObj = mapping.component as { name?: string };
  const agolResolution = componentObj?.name && config?.agolResolutions
    ? config.agolResolutions[componentObj.name]
    : undefined;
  const tables = extractComponentTables(mapping.component, connections, arcadeNotesLookup, folderDataStores, agolResolution);

  // Append the curated Velocity-side configuration table when this component
  // corresponds to a component in the generated Velocity real-time analytic.
  if (velocityModel && analytic) {
    const geoEventLabel = getGeoEventComponentLabel(mapping.component) || getComponentName(mapping.component);
    const velocityTable = buildVelocityComponentTable(velocityModel, analytic, geoEventLabel);
    if (velocityTable) {
      // The "Velocity Configuration — Tool:" table already surfaces the
      // converted Arcade expressions (expanded from the tool's field
      // calculations), so an "Arcade Migration Notes" table would duplicate
      // that content. Drop it here so the Velocity side shows only the
      // curated tool table, matching how the other tools are presented.
      if (velocityTable.title.startsWith('Velocity Configuration — Tool:')) {
        const dupIndex = tables.findIndex((t) => t.title === 'Arcade Migration Notes');
        if (dupIndex !== -1) {
          tables.splice(dupIndex, 1);
        }
      }
      tables.push(velocityTable);
    }
  }

  return {
    title: componentName,
    content: lines.join('\n'),
    diagram: neighborhoodDiagram,
    tables: tables.length > 0 ? tables : undefined,
    metadata: {
      kind: 'component',
      status: toSectionStatus(mapping.status),
      componentType: inferComponentType(mapping.component),
      searchTerms: buildComponentSearchTerms(mapping),
    },
  };
}

/**
 * Extracts configuration parameters from a GeoEvent component as tables.
 * When arcadeNotesLookup is provided, appends an "Arcade Migration Notes" table
 * for nodes that require Arcade expression conversion.
 */
function extractComponentTables(
  component: unknown,
  connections?: ArcGISServerConnection[],
  arcadeNotesLookup: Map<string, ArcadeNoteRow[]> = new Map(),
  folderDataStores: FolderDataStore[] = [],
  agolResolution?: AgolResolution,
): DocumentTable[] {
  const tables: DocumentTable[] = [];
  const obj = component as Record<string, unknown>;

  // Input or Output component
  if ('adapter' in obj && 'transport' in obj) {
    const conn = component as Input | Output;
    const isOutput = 'connector' in obj && typeof obj.connector === 'string' &&
      (obj.connector as string).includes('-out-');
    const settingsTitle = isOutput
      ? 'GeoEvent Server Output Settings'
      : 'GeoEvent Server Input Settings';

    // Merge adapter and transport properties into a single table
    const allRows = [
      ...propertyMapToRows(conn.adapter.properties, connections, folderDataStores),
      ...propertyMapToRows(conn.transport.properties, connections, folderDataStores, agolResolution),
    ];
    if (allRows.length > 0) {
      tables.push({ title: settingsTitle, rows: allRows });
    }

    return tables;
  }

  // ServiceNode (filter or processor)
  if ('filter' in obj || 'processor' in obj) {
    const node = component as ServiceNode;

    if (node.filter) {
      const filterRows = formatFilterConditionRows(node.filter.logicalCondition);
      if (filterRows.length > 0) {
        tables.push({ title: 'Filter Conditions', rows: filterRows });
      }
    }

    if (node.processor) {
      const procRows = propertyMapToRows(node.processor.properties);
      if (procRows.length > 0) {
        tables.push({ title: 'GeoEvent Server Processor Settings', rows: procRows });
      }
    }

    // Choice routers: enumerate each ordered when-clause as a route so the
    // guide shows exactly which condition sends records to which destination.
    // These map one-to-one to outputs of the Velocity Route by Expressions tool.
    if (node.choiceRoutes && node.choiceRoutes.length > 0) {
      const routeRows: DocumentTableRow[] = [...node.choiceRoutes]
        .sort((a, b) => a.order - b.order)
        .map((route) => {
          const cond = summarizeLogicalCondition(route.condition) || '(always)';
          const target = route.targetLabel || route.targetRef || 'unspecified target';
          return {
            parameter: route.label || route.name,
            value: `When ${cond} \u2192 route to "${target}"`,
          };
        });
      if (routeRows.length > 0) {
        tables.push({
          title: 'Choice Routes (one Route by Expressions output per row)',
          rows: routeRows,
        });
      }
    }

    // Append Arcade Migration Notes table if this node has expressions needing attention
    const nodeLabel = node.label || node.name;
    const arcadeRows = arcadeNotesLookup.get(nodeLabel);
    if (arcadeRows && arcadeRows.length > 0) {
      const tableRows: DocumentTableRow[] = arcadeRows.map(row => ({
        parameter: row.propertyKey,
        value: row.expression,
      }));
      tables.push({ title: 'Arcade Migration Notes', rows: tableRows });
    }

    return tables;
  }

  return tables;
}

/**
 * Converts a PropertyMap to table rows, excluding empty values.
 * Uses the parameter-labels.json lookup to display human-readable names.
 * When connections are provided, resolves datastore/dataStoreName GUIDs to human-readable labels.
 */
function propertyMapToRows(properties: PropertyMap, connections?: ArcGISServerConnection[], folderDataStores: FolderDataStore[] = [], agolResolution?: AgolResolution): DocumentTableRow[] {
  const rows: DocumentTableRow[] = [];
  const connectionKeys = new Set(['datastore', 'dataStoreName']);
  const folderDataStoreKeys = new Set(['inputFolderDataStore', 'outputFolderDataStore']);
  for (const [key, prop] of Object.entries(properties)) {
    if (prop.value !== undefined && prop.value !== null && prop.value !== '') {
      const label = paramLabelMap[key] || key;
      let displayValue = prop.value;

      // Resolve connection GUIDs to human-readable connection info
      if (connections && connectionKeys.has(key) && prop.value !== 'default') {
        const conn = connections.find(c => c.name === prop.value);
        if (conn) {
          displayValue = `${conn.label} (${conn.url})`;
        }
      }

      // Resolve folder data store GUIDs to physical paths
      if (folderDataStores.length > 0 && folderDataStoreKeys.has(key)) {
        const store = folderDataStores.find(ds => ds.name === prop.value);
        if (store) {
          displayValue = store.path;
        }
      }

      rows.push({ parameter: label, value: displayValue });

      // After Layer Reference Type, add a separate Constructed URL row
      if (key === 'referenceToLayerType' && connections) {
        const featureUrl = resolveFeatureLayerUrl(properties, connections);
        if (featureUrl) {
          rows.push({ parameter: 'Constructed URL', value: featureUrl });
        } else {
          // Detect AGOL-backed inputs and surface a binding note in
          // place of a (broken) constructed URL. When the resolver was
          // able to find a Portal Item via anonymous public search, we
          // render the resolved binding instead of a manual-action note.
          const { isAgol, connection } = isAgolBackedInput(properties, connections);
          if (isAgol && connection) {
            const svcName = String(properties['serviceName']?.value || '(unknown)');
            const svcType = String(properties['serviceType']?.value || 'FeatureServer');
            const layerIdx = String(properties['layerIndex']?.value ?? '0');
            const declared = connection.connectionType
              && connection.connectionType.toUpperCase() !== 'AGOL'
              ? ` (registered in GeoEvent as connectionType="${connection.connectionType}")`
              : '';

            if (agolResolution?.status === 'resolved' && agolResolution.portalItemId) {
              rows.push({
                parameter: 'Hosted Service Binding',
                value:
                  `ArcGIS Online hosted service${declared}.\n` +
                  `Auto-resolved Portal Item ID: ${agolResolution.portalItemId} ` +
                  `(layer ${layerIdx}).` +
                  (agolResolution.serviceUrl ? `\nResolved service URL: ${agolResolution.serviceUrl}` : '') +
                  `\n\nIf this hosted service is secured (requires credentials), Velocity ` +
                  `does not store the underlying service credentials. Wrap the service in ` +
                  `a Portal item that stores credentials and bind the feed to that item ` +
                  `instead — see "Action Required — Configure Secured Service Binding" above.`,
              });
            } else {
              const auditLine = agolResolution?.message
                ? `\n\nResolution attempt: ${agolResolution.message}`
                : '';
              rows.push({
                parameter: 'Hosted Service Binding',
                value:
                  `ArcGIS Online hosted service${declared}. The portal URL ` +
                  `${connection.url} cannot be combined with /rest/services/... to ` +
                  `produce a working Velocity feed URL — the actual service lives ` +
                  `on a per-org services{N}.arcgis.com host.\n\n` +
                  `Manual action: locate the Portal item for "${svcName}" / ${svcType} / ` +
                  `layer ${layerIdx} in the ArcGIS Online organization and bind the Velocity ` +
                  `feed by Portal Item ID. If the service is secured (requires ` +
                  `credentials) or the Velocity site federates with a different Portal, ` +
                  `wrap the service in a Portal item that stores credentials — see ` +
                  `"Action Required — Configure Secured Service Binding" above.` + auditLine,
              });
            }
          }
        }
      }
    }
  }
  return rows;
}

/**
 * Help link covering the "Add an ArcGIS Server URL item" workflow customers
 * must follow to wrap a secured or AGOL-hosted feature service in a Portal
 * item that stores credentials. Velocity binds by Portal Item ID and does
 * not store the underlying service credentials itself, so this is the
 * standard workaround for both:
 *  - Secured feature services that GeoEvent referenced with custom credentials.
 *  - ArcGIS Online hosted services consumed from a Velocity for ArcGIS Enterprise
 *    deployment whose Portal is on-prem.
 */
const PORTAL_STORED_CREDENTIALS_HELP_URL =
  'https://enterprise.arcgis.com/en/portal/latest/use/arcgis-server-services.htm';

const PORTAL_STORED_CREDENTIALS_STEPS = [
  '1. In your Portal, choose Content → Add Item → From a URL.',
  '2. Set the URL to the underlying feature service (for ArcGIS Online–hosted services, this is the services{N}.arcgis.com URL of the hosted layer).',
  '3. Choose "Store credentials with this item" and supply the credentials Velocity should use at runtime.',
  '4. Save the new item and copy its Portal Item ID.',
  '5. In the Velocity feed, set feature-layer.portalItemId to that ID and leave feature-layer.url blank.',
  `Reference: ${PORTAL_STORED_CREDENTIALS_HELP_URL}`,
].join('\n');

/**
 * The warning message displayed when a secured feature layer is detected.
 * From the Velocity Feature Layer Feed help documentation.
 */
const SECURED_FEATURE_LAYER_NOTE_ONLINE =
  'To load a secured feature layer from a URL, you must first add a service or document from a URL to ArcGIS Online and choose to store credentials. You can then browse to the registered ArcGIS Online item to select the secured feature layer.\n\n' +
  PORTAL_STORED_CREDENTIALS_STEPS;

const SECURED_FEATURE_LAYER_NOTE_ENTERPRISE =
  'To load a secured feature layer from a URL, you must first add a service or document from a URL to ArcGIS Portal and choose to store credentials. You can then browse to the registered ArcGIS Portal item to select the secured feature layer.\n\n' +
  PORTAL_STORED_CREDENTIALS_STEPS;

function getSecuredFeatureLayerNote(targetPlatform: TargetPlatform): string {
  return targetPlatform === 'velocity-enterprise'
    ? SECURED_FEATURE_LAYER_NOTE_ENTERPRISE
    : SECURED_FEATURE_LAYER_NOTE_ONLINE;
}

/**
 * Constructs the full feature service layer URL from transport properties
 * and ArcGIS Server connections.
 *
 * AGOL caveat: when the underlying connection is effectively ArcGIS Online,
 * we deliberately return an empty string. AGOL hosted feature services are
 * not addressable via `{portalUrl}/rest/services/...`; printing such a URL
 * in the migration document misleads operators into thinking the URL will
 * work in Velocity. Callers should render a "Manual Action Required" note
 * for AGOL-backed inputs instead.
 */
function resolveFeatureLayerUrl(properties: PropertyMap, connections: ArcGISServerConnection[]): string {
  const customLayerUrl = properties['customLayerUrl']?.value || '';

  // If customLayerUrl is already a full URL, use it directly
  if (customLayerUrl.startsWith('http://') || customLayerUrl.startsWith('https://')) {
    return customLayerUrl;
  }

  const datastoreGuid = properties['datastore']?.value || properties['dataStoreName']?.value || '';
  if (!datastoreGuid || datastoreGuid === 'default') return '';

  const conn = connections.find(c => c.name === datastoreGuid);
  if (!conn) return '';

  // AGOL hosted services are not addressable via {portalUrl}/rest/services/...
  if (conn.effectiveConnectionType === 'AGOL') return '';

  const baseUrl = conn.url.replace(/\/$/, '');

  if (customLayerUrl) {
    const urlPath = customLayerUrl.startsWith('/') ? customLayerUrl : `/${customLayerUrl}`;
    return `${baseUrl}${urlPath}`;
  }

  const serviceName = properties['serviceName']?.value || '';
  const serviceType = properties['serviceType']?.value || 'FeatureServer';
  const layerIndex = properties['layerIndex']?.value ?? '0';

  if (serviceName) {
    return `${baseUrl}/rest/services/${serviceName}/${serviceType}/${layerIndex}`;
  }

  return '';
}

/**
 * Returns true when the given transport properties resolve to an ArcGIS
 * Online–backed connection (whether or not GeoEvent declared it as AGOL).
 */
function isAgolBackedInput(properties: PropertyMap, connections: ArcGISServerConnection[]): { isAgol: boolean; connection?: ArcGISServerConnection } {
  const datastoreGuid = properties['datastore']?.value || properties['dataStoreName']?.value || '';
  if (!datastoreGuid || datastoreGuid === 'default') return { isAgol: false };
  const conn = connections.find(c => c.name === datastoreGuid);
  if (!conn) return { isAgol: false };
  return { isAgol: conn.effectiveConnectionType === 'AGOL', connection: conn };
}

/**
 * Formats filter logical conditions into table rows
 */
function formatFilterConditionRows(
  condition: { operator: string; conditions: unknown[] }
): DocumentTableRow[] {
  const rows: DocumentTableRow[] = [];
  rows.push({ parameter: 'Logical Operator', value: condition.operator });

  for (const sub of condition.conditions) {
    const subObj = sub as Record<string, unknown>;
    if ('operand' in subObj) {
      rows.push({
        parameter: String(subObj.operand),
        value: `${subObj.operator} ${subObj.value}`,
      });
    } else if ('operator' in subObj && 'conditions' in subObj) {
      rows.push(...formatFilterConditionRows(subObj as { operator: string; conditions: unknown[] }));
    }
  }

  return rows;
}

/**
 * Flattens a GeoEvent logical condition into a compact, human-readable string
 * (e.g. `AssignmentCode != AVAILABLE AND SecondsRemaining < 0`). Used to
 * summarize each choice route in the guide. This is a display-only rendering;
 * the executable Arcade conversion is produced separately by the generator.
 */
function summarizeLogicalCondition(condition: { operator: string; conditions: unknown[] }): string {
  const parts: string[] = [];
  for (const sub of condition.conditions) {
    const subObj = sub as Record<string, unknown>;
    if ('operand' in subObj) {
      parts.push(`${subObj.operand} ${subObj.operator} ${subObj.value}`);
    } else if ('operator' in subObj && 'conditions' in subObj) {
      const nested = summarizeLogicalCondition(subObj as { operator: string; conditions: unknown[] });
      if (nested) parts.push(`(${nested})`);
    }
  }
  const op = condition.operator?.toUpperCase();
  const joiner = op === 'OR' ? ' OR ' : op === 'NOT' ? ' AND NOT ' : ' AND ';
  return parts.join(joiner);
}

/**
 * Gets the display name for a component
 */
function getComponentName(component: unknown): string {
  if (typeof component === 'object' && component !== null) {
    const obj = component as Record<string, unknown>;
    if ('label' in obj && typeof obj.label === 'string') {
      return obj.label;
    }
    if ('name' in obj && typeof obj.name === 'string') {
      return obj.name;
    }
  }
  return 'Unknown Component';
}

/**
 * Threshold at which the Geofences subsection switches from a per-geofence
 * bullet enumeration to a per-category summary table.
 *
 * Real-world GeoEvent configurations regularly contain thousands or tens of
 * thousands of imported geofences (e.g. one per Census tract or county).
 * Enumerating each as a bullet line in the section `content` string
 * trivially exceeds {@link MAX_SECTION_BODY_TEXT} (16,000 chars) and
 * trips render-model schema validation. Beyond that, a 10k-row bullet
 * list has no migration value — geofences cannot be migrated individually
 * to Velocity; each category must be republished as a Feature Layer.
 *
 * Below this threshold we keep the existing per-geofence listing so small
 * configurations (and existing test fixtures) render unchanged.
 */
const GEOFENCE_ENUMERATION_THRESHOLD = 50;

/**
 * Maximum number of example geofence names shown per category row in the
 * summary table. Keeps each row well under MAX_CELL_TEXT regardless of
 * the underlying name length.
 */
const GEOFENCE_EXAMPLES_PER_CATEGORY = 3;

/**
 * Builds the Geofences subsection, choosing between a full per-geofence
 * enumeration (small configs) and a per-category summary table (large
 * configs). See {@link GEOFENCE_ENUMERATION_THRESHOLD}.
 */
function buildGeofencesSubsection(config: GeoEventConfiguration): DocumentSection {
  // Cross-reference geofences against sync rules to detect manually imported ones.
  // A geofence is "synced" if its category matches a sync rule's path; otherwise it
  // was manually imported and cannot be migrated without reconfiguration.
  const syncedCategories = new Set(
    config.geofenceSynchronizations.map((sync) => sync.path)
  );
  const orphaned = config.geofences.filter((f) => !syncedCategories.has(f.category));
  const synced = config.geofences.filter((f) => syncedCategories.has(f.category));

  const intro =
    'Note: Imported Geofences cannot be directly migrated to Velocity. ' +
    'Where possible, reconfigure those within GeoEvent Server as a Geofence Synchronization ' +
    'which will include source layer details.\n\n';

  if (config.geofences.length <= GEOFENCE_ENUMERATION_THRESHOLD) {
    let content = '';

    if (orphaned.length > 0) {
      content += `${orphaned.length} manually imported geofence(s) detected (no matching synchronization rule).\n` +
        'These cannot be migrated to Velocity. To resolve:\n' +
        '1. In GeoEvent Server, remove the manually imported geofences.\n' +
        '2. Publish the geofence geometries as a Feature Layer in ArcGIS Enterprise.\n' +
        '3. Configure a new Geofence Synchronization rule pointing to that Feature Layer.\n' +
        '4. Re-export the GeoEvent configuration so the synchronization details are included.\n' +
        '5. In Velocity, use the Feature Layer as a data source in a Real-Time Analytic ' +
        '(e.g., as the join input for a filter-by-geometry or join-features tool).\n\n' +
        orphaned.map((f) => `- ${f.name} (${f.category}): ${f.active ? 'active' : 'inactive'} — manually imported`).join('\n');
    }

    if (synced.length > 0) {
      if (content) content += '\n\n';
      content += `${synced.length} synchronized geofence(s) found. The Feature Layer URL from each ` +
        'synchronization rule can be used as a data source in the corresponding Velocity Real-Time Analytic.\n\n' +
        synced.map((f) => `- ${f.name} (${f.category}): ${f.active ? 'active' : 'inactive'} — synchronized`).join('\n');
    }

    return {
      title: 'Geofences',
      content: intro + content,
    };
  }

  // Large-config path: per-category summary table. One row per distinct
  // category, regardless of how many geofences populate it. This keeps the
  // section bounded — 100 categories × ~200 chars/row stays well below
  // MAX_ROWS_PER_TABLE (2000) and MAX_SECTION_BODY_TEXT (16k).
  const categoryStats = new Map<string, {
    total: number;
    active: number;
    synced: boolean;
    examples: string[];
  }>();
  for (const geofence of config.geofences) {
    let stats = categoryStats.get(geofence.category);
    if (!stats) {
      stats = {
        total: 0,
        active: 0,
        synced: syncedCategories.has(geofence.category),
        examples: [],
      };
      categoryStats.set(geofence.category, stats);
    }
    stats.total += 1;
    if (geofence.active) stats.active += 1;
    if (stats.examples.length < GEOFENCE_EXAMPLES_PER_CATEGORY) {
      stats.examples.push(geofence.name);
    }
  }

  const sortedCategories = Array.from(categoryStats.entries())
    .sort(([a], [b]) => a.localeCompare(b));

  const tableRows: DocumentTableRow[] = sortedCategories.map(([category, stats]) => {
    const status = stats.synced ? 'synchronized' : 'manually imported';
    const examplesNote = stats.examples.length < stats.total
      ? `, e.g. ${stats.examples.join(', ')}, ...`
      : stats.examples.length > 0
        ? `: ${stats.examples.join(', ')}`
        : '';
    return {
      parameter: `${category} (${status})`,
      value: `${stats.total.toLocaleString()} geofence(s), ${stats.active.toLocaleString()} active${examplesNote}`,
    };
  });

  const orphanedCategoryCount = sortedCategories.filter(([, s]) => !s.synced).length;
  const syncedCategoryCount = sortedCategories.length - orphanedCategoryCount;

  let content = intro +
    `Configuration contains ${config.geofences.length.toLocaleString()} geofence(s) across ` +
    `${sortedCategories.length} categor${sortedCategories.length === 1 ? 'y' : 'ies'}. ` +
    'Individual geofences are summarised by category below; Velocity does not migrate ' +
    'imported geofence geometries individually.\n\n';

  if (orphanedCategoryCount > 0) {
    content += `${orphaned.length.toLocaleString()} manually imported geofence(s) across ` +
      `${orphanedCategoryCount} categor${orphanedCategoryCount === 1 ? 'y' : 'ies'} ` +
      'cannot be migrated as-is. To resolve:\n' +
      '1. In GeoEvent Server, remove the manually imported geofences for each affected category.\n' +
      '2. Publish each category as a Feature Layer in ArcGIS Enterprise.\n' +
      '3. Configure a new Geofence Synchronization rule pointing to that Feature Layer.\n' +
      '4. Re-export the GeoEvent configuration so the synchronization details are included.\n' +
      '5. In Velocity, use the Feature Layer as a data source in a Real-Time Analytic ' +
      '(e.g., as the join input for a filter-by-geometry or join-features tool).';
  }

  if (syncedCategoryCount > 0) {
    if (orphanedCategoryCount > 0) content += '\n\n';
    content += `${synced.length.toLocaleString()} synchronized geofence(s) across ` +
      `${syncedCategoryCount} categor${syncedCategoryCount === 1 ? 'y' : 'ies'} ` +
      'are sourced from Feature Layers. The Feature Layer URL from each synchronization rule ' +
      'can be used as a data source in the corresponding Velocity Real-Time Analytic.';
  }

  return {
    title: 'Geofences',
    content,
    tables: [{ title: 'Geofences by Category', rows: tableRows }],
  };
}

/**
 * Builds the contextual elements section
 */
function buildContextualElements(config: GeoEventConfiguration): DocumentSection {
  const subsections: DocumentSection[] = [];

  // Folder data stores
  if (config.folderDataStores.length > 0) {
    const content = config.folderDataStores
      .map((store) => {
        const access = [];
        if (store.canRead) access.push('read');
        if (store.canWrite) access.push('write');
        return `- ${store.label || store.name}: ${store.path} (${access.join(', ')})`;
      })
      .join('\n');

    subsections.push({
      title: 'Folder Data Stores',
      content:
        'Note: Folder data stores require cloud-accessible storage in Velocity.\n\n' + content,
    });
  }

  // ArcGIS Server connections
  if (config.arcgisServerConnections.length > 0) {
    const connectionRows: DocumentTableRow[][] = config.arcgisServerConnections.map((conn) => {
      const effective = conn.effectiveConnectionType;
      const declared = conn.connectionType;
      // Pretty-print AGOL → "ArcGIS Online" for user-visible display.
      const prettify = (v: string | undefined): string =>
        v && v.toUpperCase() === 'AGOL' ? 'ArcGIS Online' : (v ?? '');
      const declaredDisplay = prettify(declared);
      const effectiveDisplay = prettify(effective);
      const typeDisplay = effective && effective.toUpperCase() !== (declared || '').toUpperCase()
        ? `${declaredDisplay} → detected as ${effectiveDisplay}${conn.agolUrlKey ? ` (org urlKey: ${conn.agolUrlKey})` : ''}`
        : declaredDisplay;
      const rows: DocumentTableRow[] = [
        { parameter: 'Label', value: conn.label },
        { parameter: 'URL', value: conn.url },
        { parameter: 'Connection Type', value: typeDisplay },
      ];
      if (effective === 'AGOL') {
        rows.push({
          parameter: 'ArcGIS Online Note',
          value:
            'This connection points at ArcGIS Online. Any Velocity feed sourced ' +
            'from this connection must be bound by Portal Item ID — hosted ' +
            'feature services in ArcGIS Online are not addressable via {portalUrl}/rest/services/...',
        });
      }
      if (conn.username) {
        rows.push({ parameter: 'Username', value: conn.username });
      }
      rows.push({ parameter: 'Password', value: '' });
      return rows;
    });

    const tables: DocumentTable[] = connectionRows.map((rows, i) => ({
      title: config.arcgisServerConnections[i]?.label || `Connection ${i + 1}`,
      rows,
    }));

    subsections.push({
      title: 'ArcGIS Server Connections',
      content:
        'Note: Server connections must be accessible from the cloud for Velocity for ArcGIS Online.',
      tables,
    });
  }

  // Geofences
  if (config.geofences.length > 0) {
    subsections.push(buildGeofencesSubsection(config));
  }

  // Geofence synchronizations
  if (config.geofenceSynchronizations.length > 0) {
    // Build a lookup of which services reference each geofence category via GeoTagger
    // or Spatial Filter processors, so we can connect sync rules to specific services.
    const categoryToServices = new Map<string, string[]>();
    for (const service of config.geoEventServices) {
      for (const node of service.nodes) {
        const uri = node.processor?.uri || '';
        if (uri.includes('GeoTagger') || uri.includes('spatial-filter') || uri.includes('SpatialFilter')) {
          const geofenceRef = node.processor?.properties['geofence']?.value || '';
          // GeoEvent geofence references use "Category/.*" or "Category/Name" patterns
          const category = geofenceRef.split('/')[0];
          if (category) {
            const existing = categoryToServices.get(category) || [];
            if (!existing.includes(service.label)) {
              existing.push(service.label);
            }
            categoryToServices.set(category, existing);
          }
        }
      }
    }

    const syncSubsections: DocumentSection[] = config.geofenceSynchronizations.map((sync) => {
      const rows: DocumentTableRow[] = [];
      rows.push({ parameter: 'Data Store', value: sync.dataStoreName });
      rows.push({ parameter: 'Service', value: sync.serviceName });
      rows.push({ parameter: 'Layer', value: sync.layer });
      if (sync.customLayerUrl) {
        rows.push({ parameter: 'Layer URL', value: sync.customLayerUrl });
      }
      if (sync.serverType) {
        rows.push({ parameter: 'Server Type', value: sync.serverType });
      }
      if (sync.referenceToLayerType) {
        rows.push({ parameter: 'Reference Type', value: sync.referenceToLayerType });
      }
      if (sync.path) {
        rows.push({ parameter: 'Path', value: sync.path });
      }
      rows.push({ parameter: 'Refresh Interval', value: `${sync.refreshInterval} seconds` });
      if (sync.nameField) {
        rows.push({ parameter: 'Name Field', value: sync.nameField });
      }
      if (sync.categoryField) {
        rows.push({ parameter: 'Category Field', value: sync.categoryField });
      }
      if (sync.wkid) {
        rows.push({ parameter: 'Spatial Reference (WKID)', value: sync.wkid });
      }
      if (sync.queryString) {
        rows.push({ parameter: 'Query', value: sync.queryString });
      }
      rows.push({ parameter: 'Incremental Update', value: String(sync.incrementalUpdate) });
      if (sync.maxAllowableOffset && sync.maxAllowableOffset !== '-1') {
        rows.push({ parameter: 'Max Allowable Offset', value: sync.maxAllowableOffset });
      }

      // Resolve/construct the Feature Layer URL for Velocity data source configuration
      let featureLayerUrl = '';
      if (sync.customLayerUrl) {
        featureLayerUrl = sync.customLayerUrl;
      } else if (sync.dataStoreName && sync.serviceName) {
        // Try to resolve via ArcGIS Server connections
        const conn = config.arcgisServerConnections.find(
          (c) => c.name === sync.dataStoreName || c.label === sync.dataStoreName
        );
        if (conn?.url) {
          const baseUrl = conn.url.replace(/\/+$/, '');
          featureLayerUrl = `${baseUrl}/rest/services/${sync.serviceName}/FeatureServer/${sync.layer || '0'}`;
        }
      }

      // Build Velocity migration guidance for this sync rule
      let guidance = 'Velocity Migration Steps:\n';
      if (featureLayerUrl) {
        guidance += `1. Verify the Feature Layer is accessible: ${featureLayerUrl}\n`;
        rows.push({ parameter: 'Resolved Feature Layer URL', value: featureLayerUrl });
      } else {
        guidance += '1. Publish the geofence geometries as a hosted Feature Layer in ArcGIS Enterprise or ArcGIS Online.\n';
      }
      guidance += '2. Register the Feature Layer as a Portal item (if not already registered).\n' +
        '3. In the Velocity Real-Time Analytic, add a Feature Layer data source and set the Portal Item ID to the registered item.\n' +
        '4. Connect the data source to the join port of the appropriate tool:\n' +
        '   - GeoTagger workflows: use the Join Features tool with a spatial join (e.g., Intersects).\n' +
        '   - Spatial Filter workflows: use the Filter by Geometry tool with the desired spatial relationship.\n' +
        '   - Enter/Exit detection: use Filter by Geometry with firstEventAsEnter or firstEventAsExit enabled.';

      // Cross-reference: which GeoEvent services reference this sync rule's category?
      const referencingServices = categoryToServices.get(sync.path) || [];
      if (referencingServices.length > 0) {
        guidance += `\n\nGeoEvent services using this geofence category ("${sync.path}"):\n` +
          referencingServices.map((s) => `- ${s}`).join('\n');
      }

      return {
        title: sync.label,
        content: guidance,
        tables: [{ title: 'Synchronization Settings', rows }],
      };
    });

    subsections.push({
      title: 'Geofence Synchronizations',
      content: `${config.geofenceSynchronizations.length} geofence synchronization rule(s) found. ` +
        'These define how geofences are kept in sync with feature service layers. ' +
        'Each synchronization rule below includes the source layer details needed to configure ' +
        'a Feature Layer data source in Velocity.',
      subsections: syncSubsections,
    });
  }

  if (subsections.length === 0) {
    return {
      title: 'Contextual Elements',
      content: 'No contextual elements (data stores, connections, geofences) found in this configuration.',
    };
  }

  return {
    title: 'Contextual Elements',
    content:
      'This section documents external resources and configurations referenced by the GeoEvent configuration.',
    subsections,
  };
}

/**
 * Builds the custom components warning section
 */
function buildCustomComponentsSection(config: GeoEventConfiguration): DocumentSection {
  const analysis = detectCustomComponents(config);

  if (!analysis.hasCustomComponents && !analysis.hasEsriAddons) {
    return {
      title: 'Custom Components',
      content:
        'No custom (non-OOTB) components were detected in this configuration. All components use standard GeoEvent Server adapters and transports.',
    };
  }

  const subsections: DocumentSection[] = [];

  if (analysis.esriAddonAdapters.length > 0 || analysis.esriAddonTransports.length > 0) {
    const addons = [...analysis.esriAddonAdapters, ...analysis.esriAddonTransports]
      .map((component) => `- ${component.uri}\n  Direction: ${component.direction}\n  Used by: ${component.usedBy.join(', ')}`)
      .join('\n\n');
    subsections.push({ title: 'Esri-built Add-ons', content: addons });
  }

  if (analysis.customAdapters.length > 0) {
    const content = analysis.customAdapters
      .map((adapter) => {
        const usedBy = adapter.usedBy.join(', ');
        return `- ${adapter.uri}\n  Direction: ${adapter.direction}\n  Used by: ${usedBy}`;
      })
      .join('\n\n');

    subsections.push({
      title: 'Custom Adapters',
      content,
    });
  }

  if (analysis.customTransports.length > 0) {
    const content = analysis.customTransports
      .map((transport) => {
        const usedBy = transport.usedBy.join(', ');
        return `- ${transport.uri}\n  Direction: ${transport.direction}\n  Used by: ${usedBy}`;
      })
      .join('\n\n');

    subsections.push({
      title: 'Custom Transports',
      content,
    });
  }

  return {
    title: analysis.hasCustomComponents ? 'Custom Components Warning' : 'Esri-built Add-ons',
    content:
      'Esri-built add-ons are evaluated separately from the core GeoEvent Server COTS baseline. Custom components also require special attention during migration as they may not have direct Velocity equivalents.',
    subsections,
  };
}

/**
 * Builds the migration recommendations section
 */
function buildImportInstructions(targetPlatform: TargetPlatform): DocumentSection {
  if (targetPlatform === 'velocity-online') {
    return {
      title: 'Importing the Velocity Configuration',
      content:
        'The generated JSON file (saved by this assistant as velocity-config.json) is imported into ' +
        'ArcGIS Velocity (Online) through the Velocity backend REST API, which is exposed as a Swagger ' +
        'interface. The steps below walk through reaching Swagger, signing in, and importing the full ' +
        'configuration in a single request.',
      subsections: [
        {
          title: 'Import the Full Configuration Through Swagger',
          content:
            'ArcGIS Velocity (Online) exposes its REST API through a Swagger interface. The Swagger UI ' +
            'lets you upload the generated velocity-config.json directly to your organization. Esri ' +
            'Technical Support documents how to reach Swagger in Knowledge Base article 000025939: ' +
            'https://support.esri.com/en-us/knowledge-base/access-swagger-the-backend-of-arcgis-velocity-000025939. ' +
            'Use the steps below in conjunction with that article.',
          subsections: [
            {
              title: 'Find Your Swagger URL',
              content:
                'The Swagger base URL is region-, instance-, and organization-specific. It follows this ' +
                'pattern:\n\n' +
                '  https://<region>-iot.arcgis.com/<instance>/<orgID>/iot/api/swagger.html#/\n\n' +
                'For example, an ArcGIS Online organization served from the North American advanced ' +
                'instance might resolve to:\n\n' +
                '  https://us-iot.arcgis.com/usadvanced00/<orgID>/iot/api/swagger.html#/\n\n' +
                'The reliable way to discover the exact <region>, <instance>, and <orgID> values for your ' +
                'organization is to capture them from network traffic, as described in the Esri Technical ' +
                'Support article linked above:\n\n' +
                '1. Sign in to https://velocity.arcgis.com with the same ArcGIS Online account you will ' +
                'use to import the configuration.\n' +
                '2. Open your browser developer tools and switch to the Network tab.\n' +
                '3. Refresh the Velocity home page and look for requests whose URL begins with ' +
                'https://<region>-iot.arcgis.com/<instance>/<orgID>/iot/...\n' +
                '4. Note the full base path up to (but not including) /iot, then append ' +
                '/iot/api/swagger.html#/ to reach the Swagger UI.',
            },
            {
              title: 'Sign In and Authorize the Swagger Session',
              content:
                'When you open the Swagger URL in a browser tab where you are already signed in to ArcGIS ' +
                'Online, Swagger will pick up your session automatically. If you see 401 Unauthorized ' +
                'responses on Try it out, click the Authorize button at the top of the Swagger UI and ' +
                'sign in again, then retry the request. The account you sign in with must have permission ' +
                'to create and manage Velocity items in the target organization.',
            },
            {
              title: 'Import the Configuration with the Configuration Rest Adapter',
              content:
                'In the Swagger UI, scroll to (or filter for) the Configuration Rest Adapter section. The ' +
                'controller name is also visible in the URL paths it groups together (everything under ' +
                '/iot/configuration). The endpoint that accepts a full configuration import is a POST that ' +
                'mirrors the Export Configuration endpoint in the same controller; in current builds it ' +
                'is exposed as POST /iot/configuration/import. If you do not see that exact path, use the ' +
                'Export operation on a small known item first to confirm the controller and request body ' +
                'shape, then look for the matching POST endpoint that accepts the same payload.\n\n' +
                'Once you have located the import endpoint:\n\n' +
                '1. Expand the POST /iot/configuration/import row in Swagger.\n' +
                '2. Click Try it out.\n' +
                '3. Open the velocity-config.json file generated by this assistant in a text editor, ' +
                'copy its full contents, and paste them into the Request body field in Swagger. If the ' +
                'endpoint instead exposes a file upload control, choose the velocity-config.json file ' +
                'directly.\n' +
                '4. Click Execute.\n' +
                '5. Check the response. A 2xx response indicates the import succeeded; the response body ' +
                'typically lists the items that were created, along with any items that were skipped or ' +
                'that failed validation.\n' +
                '6. Return to https://velocity.arcgis.com and confirm that the new feeds, real-time ' +
                'analytics, big data analytics, and data destinations appear in your item lists. Open ' +
                'each item and verify connections, secured-service credentials, Arcade expressions, and ' +
                'sample data flow before promoting to production.',
            },
            {
              title: 'Caveats',
              content:
                '- Access to the Velocity Swagger interface is restricted to users with ArcGIS Online ' +
                'administrator privileges. A standard Velocity user with publishing capabilities will not ' +
                'have the permissions required to load the Swagger page or invoke its operations. If you ' +
                'do not have administrator access, ask an ArcGIS Online administrator in your organization ' +
                'to perform the import on your behalf.\n' +
                '- The Swagger page exposes operations that can be destructive to your entire Velocity ' +
                'site. The Reset Site operation in particular wipes the configuration for the whole ' +
                'organization — not just the items owned by the signed-in administrator — and removes ' +
                'every feed, real-time analytic, big data analytic, data source, and data destination ' +
                'configured in Velocity. Several other endpoints can similarly delete or overwrite items ' +
                'across the site. Read the description and expected response of any endpoint before ' +
                'clicking Execute, and only invoke the import operation described above unless you are ' +
                'deliberately performing another administrative task.\n' +
                '- The Swagger interface itself is an Esri-hosted developer tool. Treat its responses as ' +
                'you would any other administrative API: review created items afterward and confirm they ' +
                'match expectations.\n' +
                '- Secured-data-source credentials, API keys, and stored authentication tokens are never ' +
                'embedded in the generated velocity-config.json. After import, reauthorize any feed or ' +
                'analytic that depends on a secured ArcGIS service, third-party endpoint, or token-based ' +
                'credential before starting the item.\n' +
                '- Imported items typically start in a stopped state. Start each feed and analytic ' +
                'manually after confirming the configuration is correct.\n' +
                '- The exact endpoint path, request body shape, and response format are owned by Esri and ' +
                'may evolve. Always verify the operation signature directly in the Swagger UI for your ' +
                'organization before automating the import.',
            },
          ],
        },
      ],
    };
  }

  // velocity-enterprise instructions
  return {
    title: 'Importing the Velocity Configuration',
    content:
      'The generated JSON snapshot file can be imported into ArcGIS Velocity for ArcGIS Enterprise through the ' +
      'Velocity Administrator Directory. The import process recreates all feeds and real-time analytics ' +
      'contained in the snapshot file.',
    subsections: [
      {
        title: 'Prerequisites',
        content:
          'You must sign in with a Portal administrator account. The Backup Restore Info operations ' +
          'are not available when using the Velocity primary site administrator (PSA) account because ' +
          'recreated items need to exist under a portal user, and the PSA cannot own items in ArcGIS Enterprise.',
      },
      {
        title: 'Step-by-Step Import Process',
        content:
          '1. Open a browser and navigate to the Velocity Administrator Directory:\n' +
          '   https://<your_velocity_FQDN>:7143/arcgis/admin\n' +
          '   Replace <your_velocity_FQDN> with the fully qualified domain name of the machine running ArcGIS Velocity.\n\n' +
          '2. Click the OAuth Login button. An embedded ArcGIS Enterprise sign-in screen will appear. ' +
          'Sign in with a Portal administrator account.\n\n' +
          '3. On the Administrator Directory home page, click Backup Restore Info.\n\n' +
          '4. Click Import Snapshot.\n\n' +
          '5. Click Browse and select the generated Velocity JSON snapshot file.\n\n' +
          '6. Click Import to submit the file.',
      },
      {
        title: 'Post-Import Steps',
        content:
          'After a successful import, be aware of the following:\n\n' +
          '- All feeds and real-time analytics are recreated under the Portal account that performed the import. ' +
          'An administrator may need to redistribute these items to other users.\n' +
          '- Credentials, API keys, and stored authentication tokens are not preserved in the snapshot file. ' +
          'Imported items will require re-authentication where secured data sources or services are used.\n' +
          '- Review each imported feed and real-time analytic to verify data sources, outputs, and expressions ' +
          'are configured correctly before starting them.\n' +
          '- Feature Layer feeds in the generated snapshot may not import successfully. The Velocity import API ' +
          'does not support creating Feature Layer feeds. These must be created manually in the Velocity Manager ' +
          'and linked to Real-Time Analytics after import.\n' +
          '- Real-Time Analytics that reference data sources (feature-layer enrichment) may fail to import ' +
          'silently. Review the generation warnings for affected analytics and create them manually if needed.',
      },
      {
        title: 'Additional Operations',
        content:
          'The Backup Restore Info page also provides the following operations:\n\n' +
          'Export Snapshot — Downloads a copy of the current Velocity site item configuration. ' +
          'Use this to create a backup before importing. Velocity also creates automatic backups ' +
          'every 24 hours in the configuration backup location specified during installation.\n\n' +
          'Reset Site — Deletes all existing feeds and real-time analytic items owned by users. ' +
          'WARNING: This operation is destructive and cannot be undone. Only use this if you intend to ' +
          'completely replace the current configuration.',
      },
    ],
  };
}

function buildMigrationRecommendations(
  config: GeoEventConfiguration,
  mappingResult: ConfigurationMappingResult,
  targetPlatform: TargetPlatform,
  securedInputNames: Set<string> = new Set(),
  arcadeNotesLookup: Map<string, ArcadeNoteRow[]> = new Map(),
): DocumentSection {
  const recommendations: string[] = [];
  const topologyBlockingServices = config.geoEventServices.filter((service) => {
    const topology = assessServiceTopology(buildServiceGraph(service, config));
    return topology.hasValidationBlockingTopology;
  });

  if (topologyBlockingServices.length > 0) {
    recommendations.push(
      `${topologyBlockingServices.length} analytic${topologyBlockingServices.length !== 1 ? 's' : ''} has validation-blocking topology. ` +
      'Connect or remove disconnected tools and ensure each analytic has at least one feed that reaches an output before it can be started in Velocity.'
    );
  }

  // Check for unmapped components
  if (mappingResult.unmappedComponents.length > 0) {
    recommendations.push(
      `Review ${mappingResult.unmappedComponents.length} component(s) that require manual configuration.`
    );
  }

  // Check for partial mappings
  if (mappingResult.partialMappings.length > 0) {
    recommendations.push(
      `Verify ${mappingResult.partialMappings.length} partially mapped component(s) for feature parity.`
    );
  }

  // Check for folder data stores
  if (config.folderDataStores.length > 0) {
    recommendations.push(
      'Migrate folder data stores to cloud-accessible storage (Amazon S3, Azure Blob, etc.).'
    );
  }

  // Check for geofences
  if (config.geofences.length > 0) {
    recommendations.push(
      'Recreate geofences as Feature Layers in ArcGIS Online or Enterprise Portal.'
    );
  }

  // Platform-specific recommendations
  if (targetPlatform === 'velocity-online') {
    recommendations.push(
      'Ensure all data sources are accessible from the cloud (public URLs or authenticated services).'
    );
  } else {
    recommendations.push(
      'Verify ArcGIS Enterprise 12.1+ is installed with Velocity for ArcGIS Enterprise.'
    );
  }

  // Custom components — strong callout
  const customAnalysis = detectCustomComponents(config);
  if (customAnalysis.hasEsriAddons) {
    const addonCount = customAnalysis.esriAddonAdapters.length + customAnalysis.esriAddonTransports.length;
    recommendations.push(
      `${addonCount} Esri-built GeoEvent Server add-on adapter(s) or transport(s) detected. ` +
      'Evaluate their Velocity equivalency separately from the core GeoEvent Server COTS baseline.'
    );
  }
  if (customAnalysis.hasCustomComponents) {
    const customCount = customAnalysis.customAdapters.length + customAnalysis.customTransports.length;
    recommendations.push(
      `${customCount} custom (non-OOTB) adapter(s) or transport(s) detected. Some have a known Velocity mapping in this assistant; others have no Velocity equivalent and cannot be auto-migrated. ` +
      'Engage Esri Professional Services, an Esri Partner, or your Esri account team early to evaluate alternative architectures or custom development options for the components that need a redesign.'
    );
  }

  const content =
    recommendations.length > 0
      ? recommendations.map((r, i) => `${i + 1}. ${r}`).join('\n')
      : 'No specific recommendations. The configuration appears ready for migration.';

  // --- Level of Effort estimation ---
  const componentMappings = mappingResult.mappings.filter(
    (m) => !isGeoEventServiceComponent(m.component)
  );
  const fullyMapped = componentMappings.filter((m) => m.status === 'mapped').length;
  const partialCount = mappingResult.partialMappings.length;
  const unmappedCount = mappingResult.unmappedComponents.length;
  const totalComponents = fullyMapped + partialCount + unmappedCount;

  // The Level of Effort write-up is split into two sibling subsections so
  // that the table of contents exposes a separate anchor for the static
  // legend (Level of Effort Estimate) and another for the per-configuration
  // results (Your Configuration\u2019s Level of Effort). Both anchors live
  // under Migration Recommendations.
  //
  // Software migration is a creative, problem-solving activity, so this
  // section communicates effort using a relative T-shirt scale rather than
  // fixed hours. The buckets and thresholds are unchanged; only the unit
  // of measurement is relative. The legend always lists all five sizes so
  // a casual reader can interpret any value that appears below, even
  // though most configurations will only exercise two or three sizes.
  const loeLegendLines: string[] = [
    'This section explains how the migration assistant reports the work needed to migrate this configuration ' +
    'into ArcGIS Velocity. The per-configuration results appear in the next subsection, ' +
    '\u201cYour Configuration\u2019s Level of Effort\u201d.',
    '',
    'Each part of the result is labeled with a relative effort size (XS through XL) rather than an hour or ' +
    'day estimate. Software migration is a creative, problem-solving activity \u2014 the wall-clock time it ' +
    'takes depends on the practitioner\u2019s familiarity with both products, how mature the target ' +
    'environment already is, and how thoroughly the result needs to be tested. Use the sizes to understand ' +
    'which parts of the migration are the heaviest lifts and to compare components in this configuration to ' +
    'one another; convert them into a calendar plan during your own engagement scoping.',
    '',
    'Relative effort scale:',
    '  XS \u2014 Extra Small. Quick, mostly mechanical work. A practitioner can confirm the migration is ' +
    'correct without making any design choices. Think \u201cclick through and verify\u201d.',
    '  S  \u2014 Small. A focused, well-understood task on a single component or a closely related pair. ' +
    'Configuration changes or a single expression rewrite, with no architectural questions to resolve.',
    '  M  \u2014 Medium. Multi-step work that touches several components or interacting systems (for example, ' +
    'reconnecting a feed to a secured service, then validating the data flowing through it). Requires planning ' +
    'but not redesign.',
    '  L  \u2014 Large. Substantial work that needs both design and implementation across multiple components, ' +
    'or that requires discovery (researching how Velocity handles a feature, prototyping, etc.) before the ' +
    'final approach is clear.',
    '  XL \u2014 Extra Large. Architectural redesign or multi-system integration. The original GeoEvent ' +
    'approach does not translate directly; expect iteration, stakeholder conversations, and possibly custom ' +
    'development before a final solution is agreed and tested.',
  ];

  // Per-configuration findings start here. Sized buckets are only emitted
  // when they actually apply to this configuration, so this block stays
  // empty when there is nothing to report.
  const loeLines: string[] = [];

  // Automated: fully mapped components need only import and spot-check
  if (fullyMapped > 0) {
    loeLines.push('');
    loeLines.push(`Automated (${fullyMapped} component${fullyMapped !== 1 ? 's' : ''}): ` +
      'Import the generated Velocity JSON, then verify connectivity and data flow for each feed and analytic. ' +
      'Relative effort: XS \u2014 primarily validation and smoke testing.');
  }

  if (topologyBlockingServices.length > 0) {
    loeLines.push('');
    loeLines.push(
      `Validation-Blocking Topology (${topologyBlockingServices.length} analytic${topologyBlockingServices.length !== 1 ? 's' : ''}): ` +
      'Connect or remove disconnected tools and verify that at least one feed sends data through to an output. ' +
      'Relative effort: M (Medium) - multi-step analytic correction and end-to-end data-flow validation required before starting.'
    );
  }

  // Manual review: partial mappings need Arcade rewrites, re-auth, etc.
  if (partialCount > 0) {
    const reviewSize = partialCount <= 3 ? 'S (Small)' : partialCount <= 10 ? 'M (Medium)' : 'L (Large)';
    loeLines.push('');
    loeLines.push(`Manual Review (${partialCount} component${partialCount !== 1 ? 's' : ''}): ` +
      'These components have a Velocity equivalent but require hands-on adjustments. Common tasks include: ' +
      'rewriting GeoEvent expressions to Arcade, re-authenticating secured data sources (Feature Layers, ArcGIS Server connections), ' +
      'and adjusting networking for cloud-accessible endpoints. ' +
      `Relative effort: ${reviewSize} \u2014 scoped configuration work across the affected components.`);
  }

  // Unmapped: require alternative architecture or PS engagement
  if (unmappedCount > 0) {
    const redesignSize = unmappedCount <= 2 ? 'M (Medium)' : unmappedCount <= 5 ? 'L (Large)' : 'XL (Extra Large)';
    loeLines.push('');
    loeLines.push(`Requires Redesign (${unmappedCount} component${unmappedCount !== 1 ? 's' : ''}): ` +
      'No direct Velocity equivalent exists. Each component requires evaluation for alternative approaches \u2014 ' +
      'this may involve different Velocity tool chains, external middleware, or custom development. ' +
      `Relative effort per component: ${redesignSize} \u2014 analysis-heavy, expect iteration before settling on a target architecture.`);
  }

  // Custom components escalation
  if (customAnalysis.hasCustomComponents) {
    // Build the breakdown: of the inputs/outputs that reference a custom
    // adapter or transport, how many already have an automated mapping in
    // this assistant versus how many still need architectural review? The
    // detector returns adapter/transport URIs along with the labels/names of
    // the components that use them; cross-reference those names against the
    // mapping outcomes to keep the "some" qualifier honest.
    const customUsingNames = new Set<string>();
    for (const adapter of customAnalysis.customAdapters) {
      for (const usedBy of adapter.usedBy) customUsingNames.add(usedBy);
    }
    for (const transport of customAnalysis.customTransports) {
      for (const usedBy of transport.usedBy) customUsingNames.add(usedBy);
    }
    let customAutoMapped = 0;
    let customNeedsReview = 0;
    for (const m of componentMappings) {
      const c = m.component as { name?: string; label?: string };
      const key = c.label || c.name || '';
      if (!customUsingNames.has(key)) continue;
      if (m.status === 'mapped') customAutoMapped++;
      else customNeedsReview++;
    }

    loeLines.push('');
    loeLines.push(
      'Custom Components: This configuration uses non-standard adapters, transports, or processors that are not part ' +
      'of the out-of-the-box GeoEvent Server distribution. Some custom components cannot be automatically migrated ' +
      'and may require architectural review. Contact Esri Professional Services, Esri Partners, or your Esri ' +
      'account team to scope this work before finalizing the migration plan.'
    );
    if (customUsingNames.size > 0) {
      loeLines.push(
        `Of the ${customUsingNames.size} component${customUsingNames.size !== 1 ? 's' : ''} using custom adapters or transports, ` +
        `${customAutoMapped} ${customAutoMapped === 1 ? 'has' : 'have'} an automated mapping in this assistant and ` +
        `${customNeedsReview} require${customNeedsReview === 1 ? 's' : ''} architectural review.`
      );
    }
  }

  // Geofence effort
  if (config.geofences.length > 0) {
    const syncCount = config.geofenceSynchronizations.length;
    const manualFences = config.geofences.length;
    const fenceSize = manualFences <= 5 ? 'S (Small)' : 'M (Medium)';
    loeLines.push('');
    loeLines.push(
      `Geofences (${manualFences} geofence${manualFences !== 1 ? 's' : ''}, ${syncCount} synchronization rule${syncCount !== 1 ? 's' : ''}): ` +
      'Imported geofences must be recreated as Feature Layers. Geofence synchronization rules provide source layer details ' +
      'that can be used directly in Velocity join operations. ' +
      `Relative effort: ${fenceSize} \u2014 feature layer setup and validation.`
    );
  }

  // Secured / inaccessible service effort. Velocity does not store the
  // underlying credentials for secured feature services, so each one must
  // be wrapped in a Portal item with stored credentials before the feed
  // can be bound. Inputs detected as ArcGIS Online–backed without an
  // existing portalItemId fall into the same workflow when the AGOL
  // resolver could not auto-resolve the public Portal item.
  const securedCount = securedInputNames.size;
  const unresolvedAgolInputs = (config.agolResolutions
    ? Object.entries(config.agolResolutions).filter(([name, res]) =>
        !securedInputNames.has(name) &&
        res.status !== 'resolved' &&
        res.status !== 'not-applicable')
    : []
  ).length;
  const portalItemTouchCount = securedCount + unresolvedAgolInputs;
  if (portalItemTouchCount > 0) {
    const detail: string[] = [];
    if (securedCount > 0) {
      detail.push(`${securedCount} secured feature service input${securedCount !== 1 ? 's' : ''}`);
    }
    if (unresolvedAgolInputs > 0) {
      detail.push(`${unresolvedAgolInputs} ArcGIS Online–hosted input${unresolvedAgolInputs !== 1 ? 's' : ''} that could not be auto-resolved`);
    }
    loeLines.push('');
    loeLines.push(
      `Secured / Stored-Credential Services (${detail.join(' + ')}): ` +
      'Velocity does not store the underlying service credentials. For each item, create a ' +
      'Portal item that wraps the service and stores credentials, then bind the Velocity feed ' +
      'to that Portal Item ID. See ' +
      'https://enterprise.arcgis.com/en/portal/latest/use/arcgis-server-services.htm. ' +
      `Relative effort per item: S (Small); plan ${portalItemTouchCount} S-sized task${portalItemTouchCount !== 1 ? 's' : ''} (one per item).`
    );
  }

  // Arcade expression conversion effort. The arcadeNotesLookup the doc
  // builder produced from the generated Velocity JSON already filters to
  // rows tagged ACTION REQUIRED, VERIFY, or carrying $feature references.
  // Per-component bucketing:
  //   - manual: any row contains ACTION REQUIRED
  //   - confident: at least one row, none contain ACTION REQUIRED
  let confidentArcadeNodes = 0;
  let manualArcadeNodes = 0;
  for (const rows of arcadeNotesLookup.values()) {
    if (rows.length === 0) continue;
    if (rows.some((r) => r.expression.includes('ACTION REQUIRED'))) {
      manualArcadeNodes++;
    } else {
      confidentArcadeNodes++;
    }
  }
  if (confidentArcadeNodes > 0 || manualArcadeNodes > 0) {
    loeLines.push('');
    const parts: string[] = [];
    if (confidentArcadeNodes > 0) {
      parts.push(
        `${confidentArcadeNodes} component${confidentArcadeNodes !== 1 ? 's' : ''} ` +
          `with auto-converted Arcade expressions (relative effort: XS per component — verify the generated expression matches your intent)`,
      );
    }
    if (manualArcadeNodes > 0) {
      parts.push(
        `${manualArcadeNodes} component${manualArcadeNodes !== 1 ? 's' : ''} ` +
          `flagged ACTION REQUIRED (manual GeoEvent → Arcade rewrite, relative effort: S per component)`,
      );
    }
    loeLines.push(
      `Arcade Expression Conversion (${parts.join('; ')}): ` +
        'Use the Velocity Arcade profile and Arcade Playground to refine expressions before going live. ' +
        'References: ' +
        'https://developers.arcgis.com/arcade/profiles/velocity/, ' +
        'https://developers.arcgis.com/arcade/playground/, ' +
        'https://doc.arcgis.com/en/iot/analyze/use-arcade-expressions.htm.',
    );
  }

  // Overall complexity summary
  loeLines.push('');
  if (topologyBlockingServices.length > 0 && unmappedCount === 0 && !customAnalysis.hasCustomComponents) {
    loeLines.push(`Overall Complexity: Moderate - ${topologyBlockingServices.length} analytic${topologyBlockingServices.length !== 1 ? 's require' : ' requires'} topology corrections before it can be started.`);
  } else if (unmappedCount === 0 && partialCount === 0 && !customAnalysis.hasCustomComponents) {
    loeLines.push(`Overall Complexity: Low — all ${totalComponents} component(s) are fully compatible. ` +
      'Migration is primarily an import-and-validate exercise.');
  } else if (unmappedCount === 0 && !customAnalysis.hasCustomComponents) {
    loeLines.push(`Overall Complexity: Moderate — ${fullyMapped} of ${totalComponents} component(s) are fully automated, ` +
      `with ${partialCount} requiring manual review.`);
  } else {
    loeLines.push(`Overall Complexity: High — ${unmappedCount} component(s) require redesign` +
      (customAnalysis.hasCustomComponents ? ' and custom components were detected' : '') +
      '. Plan for iterative testing and consider engaging Esri Professional Services or Esri Partners.');
  }

  const subsections: DocumentSection[] = [
    {
      title: 'Level of Effort Estimate',
      content: loeLegendLines.join('\n'),
    },
    {
      title: 'Your Configuration\u2019s Level of Effort',
      // `loeLines` is built by the bucket emitters above. Each bucket
      // pushes its own leading blank line for separation, so trim the
      // first blank to keep the rendered subsection tidy.
      content: (loeLines[0] === '' ? loeLines.slice(1) : loeLines).join('\n'),
    },
  ];

  return {
    title: 'Migration Recommendations',
    content,
    subsections,
  };
}

/**
 * Arcade migration note for a single tool property
 */
interface ArcadeNoteRow {
  propertyKey: string;
  expression: string;
}

function redactDocumentTable(table: DocumentTable): DocumentTable {
  return {
    title: redactSensitiveText(table.title),
    rows: table.rows.map((row) => ({
      parameter: redactSensitiveText(row.parameter),
      value: String(redactSensitiveValue(row.parameter, row.value)),
    })),
  };
}

function redactDocumentSection(section: DocumentSection): DocumentSection {
  return {
    ...section,
    title: redactSensitiveText(section.title),
    content: redactSensitiveText(section.content),
    subsections: section.subsections?.map(redactDocumentSection),
    tables: section.tables?.map(redactDocumentTable),
    linkedItems: section.linkedItems?.map((item) => ({
      ...item,
      label: redactSensitiveText(item.label),
      children: item.children?.map((child) => ({
        ...child,
        label: redactSensitiveText(child.label),
      })),
    })),
  };
}

/**
 * Builds the "Arcade Expression Language Reference" section for the migration guide.
 * Provides users with an overview of Arcade in Velocity, profile variables,
 * a GeoEvent→Arcade function mapping table, null-handling best practices,
 * track functions, and links to external documentation.
 */
function buildArcadeExpressionReference(mappingResult: ConfigurationMappingResult): DocumentSection {
  // Determine if any components actually need Arcade work
  const partialCount = mappingResult.partialMappings.length;

  const overview =
    'ArcGIS Velocity uses ArcGIS Arcade as its expression language for filter conditions, field calculations, ' +
    'incident detection, join conditions, and more. Arcade replaces GeoEvent Server\'s proprietary expression ' +
    'syntax and provides a portable, lightweight scripting language shared across the ArcGIS platform.\n\n' +
    'When migrating from GeoEvent Server, any component that uses expressions — filters, field calculators, ' +
    'incident detectors, and regex processors — requires those expressions to be rewritten in Arcade.' +
    (partialCount > 0
      ? ` This configuration has ${partialCount} partially-mapped component(s) that may require Arcade rewrites.`
      : '');

  const subsections: DocumentSection[] = [
    {
      title: 'Velocity Arcade Profile',
      content:
        'Velocity implements the Velocity Arcade profile (since Arcade v1.12). This profile defines which ' +
        'variables, functions, and return types are available when writing expressions in Velocity tools.\n\n' +
        'Available function bundles:\n' +
        '  - Core: Array, Date, Dictionary, Feature, Logical, Math, Text functions\n' +
        '  - Geometry: Area, Length, Distance, Buffer, Centroid, Extent, Generalize, and more\n' +
        '  - Track: TrackCurrentSpeed, TrackCurrentDistance, TrackFieldWindow, and more\n\n' +
        'Supported return types: Boolean, Date, Geometry, Number, Text\n\n' +
        'The default time zone for Velocity expressions is UTC.',
    },
    {
      title: 'Profile Variables',
      content:
        'Velocity Arcade expressions use these profile variables to access event data:\n\n' +
        '  - $feature — The incoming event. Used in all tools except Join Features for the join condition.\n' +
        '    Access fields with $feature["fieldName"] or $feature.fieldName.\n\n' +
        '  - $target — In Join Features, the event from the primary (target) input being evaluated.\n\n' +
        '  - $join — In Join Features, the event from the secondary (join) input.\n\n' +
        '  - $analytic — Dictionary with analytic metadata:\n' +
        '    $analytic.AnalyticStartTime — epoch timestamp of when the analytic started\n' +
        '    $analytic.AnalyticLastEndTime — epoch timestamp of the last time the analytic stopped\n' +
        '    Convert to date with Date($analytic.AnalyticStartTime).',
    },
    {
      title: 'GeoEvent to Arcade Function Mapping',
      content:
        'The following table maps common GeoEvent Server expression functions to their Arcade equivalents. ' +
        'This migration utility auto-converts simple cases, but complex expressions may need manual adjustment.',
      tables: [{
        title: 'GeoEvent → Arcade Function Reference',
        rows: [
          { parameter: 'Field reference: ${fieldName}', value: '$feature["fieldName"]' },
          { parameter: 'toString(value)', value: 'Text(value)' },
          { parameter: 'toInteger(value) / toDouble(value) / toLong(value)', value: 'Number(value)' },
          { parameter: 'concat(a, b)', value: 'Concatenate([a, b], separator)' },
          { parameter: 'substring(text, start, end)', value: 'Mid(text, start, length)' },
          { parameter: 'toUpper(text) / toLower(text)', value: 'Upper(text) / Lower(text)' },
          { parameter: 'trim(text)', value: 'Trim(text)' },
          { parameter: 'indexOf(text, search)', value: 'Find(search, text)' },
          { parameter: 'replaceAll(text, search, replace)', value: 'Replace(text, search, replace)' },
          { parameter: 'abs(n) / ceil(n) / floor(n) / round(n)', value: 'Abs(n) / Ceil(n) / Floor(n) / Round(n)' },
          { parameter: 'sqrt(n) / log(n) / pow(base, exp)', value: 'Sqrt(n) / Log(n) / Pow(base, exp)' },
          { parameter: 'min(a, b) / max(a, b)', value: 'Min(a, b) / Max(a, b)' },
          { parameter: 'hasField(fieldName)', value: 'HasValue($feature, "fieldName")' },
          { parameter: 'isNull(value)', value: 'IsEmpty(value)' },
          { parameter: 'now()', value: 'Now()' },
          { parameter: 'AND / OR / NOT', value: '&& / || / !' },
          { parameter: '= (equality)', value: '==' },
          { parameter: 'field IN (singleValue)', value: 'field == singleValue   (e.g. $feature["status"] == 3)' },
          { parameter: 'field IN (v1, v2, v3)', value: 'Includes([v1, v2, v3], field)   (e.g. Includes([1, 2, 3], $feature["status"]))' },
          { parameter: 'field NOT IN (v1, v2, v3)', value: '!Includes([v1, v2, v3], field)' },
          { parameter: 'field EXISTS / field IS NOT NULL', value: '!IsEmpty(field)   (e.g. !IsEmpty($feature["status"]))' },
          { parameter: 'field NOT EXISTS / field IS NULL', value: 'IsEmpty(field)' },
          { parameter: 'field ISNULL (GeoEvent operator)', value: 'field == null   (e.g. $feature["example"] == null)' },
          { parameter: 'field NOTNULL (GeoEvent operator)', value: 'field != null' },
          { parameter: 'Regex match', value: 'No direct equivalent — use Find(), Replace(), or Mid() for pattern matching' },
          { parameter: 'GeoEvent Tag references', value: 'Use field names directly: $feature["fieldName"]' },
        ],
      }],
    },
    {
      title: 'Tools That Use Arcade Expressions',
      content:
        'The following Velocity tools accept Arcade expressions as input parameters:\n\n' +
        '  - Calculate Field — Create or update field values. Supports $feature and Track functions.\n' +
        '  - Map Fields — Remap and transform the event schema. Supports $feature and Track functions.\n' +
        '  - Filter by Expression — Keep or discard events based on a Boolean expression.\n' +
        '  - Detect Incidents — Open/close conditions use Arcade Boolean expressions.\n' +
        '  - Join Features — Join conditions use $target and $join to match events between inputs.\n' +
        '  - Create Buffers — Distance expressions using $feature (in "By Expression" mode).\n' +
        '  - Reconstruct Tracks — Track-aware field calculations.\n\n' +
        'All tools that use Arcade support the arcadeSampleValues property for providing test data to the ' +
        'expression builder. The generated configuration pre-populates sample values based on the GeoEvent ' +
        'Definition schema when available.',
    },
    {
      title: 'Track Functions',
      content:
        'Velocity provides Arcade Track functions for working with sequentially ordered features. These are ' +
        'available only in Calculate Field and Map Fields tools.\n\n' +
        'Key track functions:\n' +
        '  - TrackCurrentSpeed() — Speed between current and previous observation\n' +
        '  - TrackCurrentDistance() — Cumulative distance from track origin\n' +
        '  - TrackCurrentAcceleration() — Acceleration based on speed changes\n' +
        '  - TrackFieldWindow("field", startIdx, endIdx) — Array of field values over a window\n' +
        '  - TrackGeometryWindow(startIdx, endIdx) — Array of geometries over a window\n' +
        '  - TrackDuration() — Duration from the first observation to the current\n' +
        '  - TrackIndex() — Index of the current feature in the track\n' +
        '  - TrackStartTime() — Timestamp of the first observation\n\n' +
        'Important constraints:\n' +
        '  - Real-time analytics can only access previous (-x) and current (0) track values, not future values\n' +
        '  - Previous values are limited to 5 features back (-5)\n' +
        '  - This is controlled by the featuresPerTrack property (default: 6, representing current + 5 previous)\n\n' +
        'These functions are particularly useful for migrating GeoEvent processors that compute speed, distance, ' +
        'bearing, or detect changes between consecutive events.',
    },
    {
      title: 'Null Value Handling',
      content:
        'Arcade treats null values as 0 in mathematical operations. This is a common source of unexpected ' +
        'behavior during migration. Always guard numeric comparisons with explicit null checks.\n\n' +
        'Example — incorrect (null passes as 0):\n' +
        '  $feature["distance"] <= 5\n\n' +
        'Example — correct (null-safe):\n' +
        '  $feature["distance"] != null && $feature["distance"] <= 5\n\n' +
        'Other null-handling patterns:\n' +
        '  - IsEmpty($feature["field"]) — returns true if field is null or empty string\n' +
        '  - IIF(IsEmpty($feature["field"]), defaultValue, $feature["field"]) — provide a fallback\n' +
        '  - DefaultValue($feature["field"], 0) — returns 0 if field is null',
    },
    {
      title: 'Best Practices',
      content:
        'When writing Arcade expressions for Velocity analytics:\n\n' +
        '  - Keep expressions simple and short. Complex scripts impact analytic processing speed.\n' +
        '  - Avoid loops. While supported, looping routines can severely reduce performance. Use built-in ' +
        'array functions (Filter, Map, Reduce) instead of explicit iteration.\n' +
        '  - Test with sample values. The Arcade expression builder uses default sample values (e.g., "Pacific" ' +
        'for strings) that may not parse correctly for your data. Update sample values in the $feature tab to ' +
        'match your actual data formats.\n' +
        '  - Handle nulls explicitly. See the null value handling section above.\n' +
        '  - Use the Arcade Playground (https://developers.arcgis.com/arcade/playground/) to prototype and debug ' +
        'expressions in an interactive environment before applying them in Velocity.',
    },
    {
      title: 'Arcade Documentation Resources',
      content:
        'Use the following resources when writing and converting Arcade expressions:\n\n' +
        '  - ArcGIS Arcade Language Reference: https://developers.arcgis.com/arcade/\n' +
        '  - Velocity Arcade Profile (variables, functions, return types): https://developers.arcgis.com/arcade/profiles/velocity/\n' +
        '  - Using Arcade in Velocity: https://doc.arcgis.com/en/iot/analyze/use-arcade-expressions.htm\n' +
        '  - Track Functions in Velocity: https://doc.arcgis.com/en/velocity/analyze/use-track-functions.htm\n' +
        '  - Arcade Best Practices for Velocity: https://doc.arcgis.com/en/velocity/analyze/best-practices-for-arcade-expressions.htm\n' +
        '  - Arcade Function Reference: https://developers.arcgis.com/arcade/function-reference/\n' +
        '  - Arcade Playground (interactive testing): https://developers.arcgis.com/arcade/playground/\n' +
        '  - Arcade in ArcGIS Maps SDK for JavaScript: https://developers.arcgis.com/javascript/latest/arcade/',
    },
  ];

  return {
    title: 'Arcade Expression Language Reference',
    content: overview,
    subsections,
  };
}

/**
 * Builds a lookup map from tool label → Arcade migration note rows.
 * Extracts expression-related properties from the generated Velocity configuration
 * that contain action-required markers or auto-converted Arcade expressions.
 */
function buildArcadeNotesLookup(velocityConfig: VelocityConfiguration): Map<string, ArcadeNoteRow[]> {
  const lookup = new Map<string, ArcadeNoteRow[]>();

  for (const realtimeJson of velocityConfig.realtime) {
    let analytic: { tools?: Array<{ label?: string; name?: string; properties?: Record<string, unknown> }> };
    try {
      analytic = JSON.parse(realtimeJson);
    } catch {
      continue;
    }

    for (const tool of analytic.tools || []) {
      const toolLabel = tool.label || tool.name || '';
      const props = tool.properties || {};
      const rows: ArcadeNoteRow[] = [];

      for (const [key, val] of Object.entries(props)) {
        if (key === 'ui' || key === 'arcadeSampleValues') continue;

        const strVal = typeof val === 'string' ? val : '';

        // Check for expression properties with action-required or auto-converted content
        if (strVal.includes('ACTION REQUIRED') || strVal.includes('VERIFY') || strVal.includes('$feature[')) {
          rows.push({
            propertyKey: key.replace(/^[^.]+\./, ''),
            expression: strVal,
          });
          continue;
        }

        // Check for JSON-encoded values (e.g., fieldCalculations array)
        if (typeof val === 'string' && val.startsWith('[')) {
          try {
            const parsed = JSON.parse(val) as Array<{ expression?: string }>;
            for (const item of parsed) {
              if (item.expression && (item.expression.includes('ACTION REQUIRED') || item.expression.includes('VERIFY') || item.expression.includes('$feature['))) {
                rows.push({
                  propertyKey: key.replace(/^[^.]+\./, ''),
                  expression: item.expression,
                });
              }
            }
          } catch {
            // Not valid JSON array — skip
          }
        }
      }

      if (rows.length > 0) {
        lookup.set(toolLabel, rows);
      }
    }
  }

  return lookup;
}

export function buildMigrationDocument(
  config: GeoEventConfiguration,
  mappingResult: ConfigurationMappingResult,
  diagrams: Map<string, MermaidDiagram>,
  velocityConfig: VelocityConfiguration,
  targetPlatform: TargetPlatform,
  securedInputNames: Set<string> = new Set(),
  generationWarnings: string[] = [],
  migrationStrategy: MigrationStrategy = 'side-by-side',
  options: MigrationDocumentOptions = {}
): MigrationDocument {
  // Build lookup of Arcade migration notes keyed by tool label (which matches node label)
  const arcadeNotesLookup = buildArcadeNotesLookup(velocityConfig);

  // Parse the generated Velocity configuration so the guide can surface the
  // Velocity-side parameters, Arcade expressions, and model alongside GeoEvent.
  const velocityModel = buildVelocityModel(velocityConfig);

  const sections: DocumentSection[] = [
    buildIntroduction(targetPlatform),
    buildExecutiveSummary(config, mappingResult, targetPlatform, options),
    buildMigrationRecommendations(config, mappingResult, targetPlatform, securedInputNames, arcadeNotesLookup),
    buildServiceMigrationDetails(config, mappingResult, diagrams, securedInputNames, targetPlatform, arcadeNotesLookup, migrationStrategy, velocityModel),
    buildContextualElements(config),
    buildCustomComponentsSection(config),
    buildArcadeExpressionReference(mappingResult),
  ];

  // Add validation adjustments section if there are generation warnings
  const validationWarnings = generationWarnings.filter(
    w => w.includes('adjusted to') || w.includes('meet Velocity')
  );
  if (validationWarnings.length > 0) {
    sections.push({
      title: 'Configuration Validation Adjustments',
      content: 'The following adjustments were made to the generated Velocity configuration to comply with Velocity validation rules. ' +
        'Review these changes and verify they are acceptable for your deployment.',
      tables: [{
        title: 'Adjustments',
        rows: validationWarnings.map((w, i) => ({
          parameter: `Adjustment ${i + 1}`,
          value: w,
        })),
      }],
    });
  }

  const redactionWarnings = generationWarnings.filter(
    w => w.includes('Sensitive credential-like values were redacted')
  );
  if (redactionWarnings.length > 0) {
    sections.push({
      title: 'Security Redactions',
      content:
        'Sensitive credential-like values were removed from generated artifacts. ' +
        'After import, manually re-enter passwords, tokens, API keys, authorization headers, OAuth secrets, and similar values in Velocity.',
    });
  }

  sections.push(buildImportInstructions(targetPlatform));

  const document: MigrationDocument = {
    title: 'ArcGIS Velocity\nMigration Guide',
    generatedDate: new Date(),
    targetPlatform,
    sourceFileName: options.sourceFileName,
    sections,
  };

  return {
    ...document,
    title: redactSensitiveText(document.title),
    sections: document.sections.map(redactDocumentSection),
  };
}

// ---------------------------------------------------------------------------
// Document → Render-model adapter (Phase 5 cutover)
//
// The browser pipeline (src/client/migration/pipeline.ts) builds a full
// `MigrationDocument` and then converts it through this adapter so the
// stateless `POST /api/render` endpoint receives a schema-valid
// `MigrationReportModel`. The inverse adapter
// (`buildMigrationDocumentFromModel`) lives in `./render-model-adapter.ts`
// because it is server-only — it decodes PNG data URLs via Node's
// `Buffer` and is dead-code-eliminated from the wizard bundle.
//
// Diagrams round-trip as the structured graph payload defined in
// `diagramGraphSchema` (node id/label/kind + edge from/to/label). The
// browser cannot rasterize PNGs without a heavy mermaid/canvas bundle,
// so the server rasterizes graph-only diagrams via the existing
// `renderFlowDiagramPng` (node-canvas) at PDF / DOCX / HTML render time.
// ---------------------------------------------------------------------------

export interface DocumentToModelOptions {
  summary: MigrationReportModel['summary'];
  migrationStrategy: MigrationReportModel['migrationStrategy'];
  warnings?: MigrationReportModel['warnings'];
}

export function buildRenderModelFromDocument(
  doc: MigrationDocument,
  options: DocumentToModelOptions,
): MigrationReportModel {
  const diagramCollector = new DiagramCollector();
  return {
    schemaVersion: 1,
    title: doc.title,
    generatedAtIso: doc.generatedDate.toISOString(),
    sourceFilenameDisplay: doc.sourceFileName,
    targetPlatform: doc.targetPlatform,
    migrationStrategy: options.migrationStrategy,
    summary: options.summary,
    sections: doc.sections.map((s) => adaptSectionToModel(s, diagramCollector)),
    warnings: options.warnings,
    diagrams: diagramCollector.collected.length > 0 ? diagramCollector.collected : undefined,
  };
}

/**
 * Allocates stable diagram ids and collects unique diagram assets so
 * sections can reference them by `diagramId`. Two diagrams with the
 * same structural graph and title share an asset id.
 */
class DiagramCollector {
  readonly collected: RmDiagramAsset[] = [];
  private nextIndex = 0;
  private readonly used = new Set<string>();

  add(diagram: { syntax: string; graph?: ServiceGraph }, sectionAnchorId: string | undefined, sectionTitle: string): string | undefined {
    if (!diagram.graph || diagram.graph.nodes.length === 0) return undefined;
    const baseId = sectionAnchorId
      ? `dg-${sectionAnchorId}`.replace(/[^a-zA-Z0-9_-]/g, '-').toLowerCase()
      : `dg-${this.nextIndex}`;
    let id = baseId;
    let suffix = 1;
    while (this.used.has(id)) {
      id = `${baseId}-${suffix}`;
      suffix += 1;
    }
    this.used.add(id);
    this.nextIndex += 1;

    const graphPayload: RmDiagramGraph = {
      nodes: diagram.graph.nodes.slice(0, 5000).map((n) => ({
        id: clipAnchor(n.id),
        label: clipPlain(n.label, 400),
        kind: n.type,
      })),
      edges: diagram.graph.edges.slice(0, 10_000).map((e) => ({
        from: clipAnchor(e.from),
        to: clipAnchor(e.to),
      })),
    };

    this.collected.push({
      id,
      title: clipPlain(sectionTitle || 'Flow diagram', 400),
      graph: graphPayload,
    });
    return id;
  }
}

function clipAnchor(s: string): string {
  // Anchor schema: [a-zA-Z0-9_-]+ length 1..128
  const cleaned = s.replace(/[^a-zA-Z0-9_-]/g, '-');
  const trimmed = cleaned.length > 128 ? cleaned.slice(0, 128) : cleaned;
  return trimmed.length > 0 ? trimmed : 'node';
}

function clipPlain(s: string, max: number): string {
  // Trim control chars and clip to max length to satisfy plainText schema.
  // eslint-disable-next-line no-control-regex
  const stripped = s.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '');
  return stripped.length > max ? stripped.slice(0, max) : stripped;
}

const CELL_TRUNCATION_MARKER = '… [truncated]';

/**
 * Defensive backstop for table cells. The render-model schema caps each cell
 * at {@link MAX_CELL_TEXT}; clip here (with a visible marker) so no synthesized
 * value can fail validation at the `/api/render` boundary. Individual builders
 * should still emit readable, per-item rows — this only guards against a
 * pathological one-off value slipping through.
 */
function clipCell(s: string): string {
  if (s.length <= MAX_CELL_TEXT) return s;
  return s.slice(0, MAX_CELL_TEXT - CELL_TRUNCATION_MARKER.length) + CELL_TRUNCATION_MARKER;
}

function adaptSectionToModel(section: DocumentSection, diagrams: DiagramCollector): RmSection {
  const out: RmSection = {
    title: section.title,
    content: section.content,
  };
  if (section.anchorId) out.anchorId = section.anchorId;
  if (section.subsections && section.subsections.length > 0) {
    out.subsections = section.subsections.map((sub) => adaptSectionToModel(sub, diagrams));
  }
  if (section.tables && section.tables.length > 0) {
    out.tables = section.tables.map((t) => ({
      title: t.title,
      rows: t.rows.map((r) => ({ parameter: clipCell(r.parameter), value: clipCell(r.value) })),
    }));
  }
  if (section.linkedItems && section.linkedItems.length > 0) {
    out.linkedItems = section.linkedItems.map(adaptLinkedItemToModel);
  }
  if (section.diagram) {
    const diagramId = diagrams.add(section.diagram, section.anchorId, section.title);
    if (diagramId) {
      out.diagram = { diagramId };
    }
  }
  if (section.metadata) {
    const md: NonNullable<RmSection['metadata']> = {};
    if (section.metadata.kind) md.kind = section.metadata.kind;
    if (section.metadata.status) md.status = section.metadata.status;
    if (section.metadata.componentType) md.componentType = section.metadata.componentType;
    if (section.metadata.searchTerms && section.metadata.searchTerms.length > 0) {
      md.searchTerms = section.metadata.searchTerms
        .map((t) => clipPlain(t, 200))
        .filter((t) => t.length > 0)
        .slice(0, 64);
    }
    if (md.kind || md.status || md.componentType || (md.searchTerms && md.searchTerms.length > 0)) {
      out.metadata = md;
    }
  }
  return out;
}

function adaptLinkedItemToModel(item: RmLinkedItem): RmLinkedItem {
  const out: RmLinkedItem = {
    label: item.label,
    anchorId: item.anchorId,
  };
  if (item.children && item.children.length > 0) {
    out.children = item.children.map(adaptLinkedItemToModel);
  }
  return out;
}
